import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import * as XLSXStyle from 'xlsx-js-style';
import RecipesService from '../services/recipesService';
import FeaturesService from '../services/FeaturesService';
import InventoryService from '../services/inventoryService';
import { listRecipeReviewDrafts } from './recipeReviewDrafts';
import { getCachedBusinessName } from './businessIdentity';

const normalizeText = (value) => String(value || '').trim();
const normalizeKey = (value) => normalizeText(value).toLowerCase();

const toTitleCase = (value) => {
  const normalized = normalizeText(value);
  if (!normalized) return '';

  return normalized
    .split(/\s+/)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`)
    .join(' ');
};

const formatNumber = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0';
  if (Number.isInteger(numeric)) return String(numeric);
  return String(Number(numeric.toFixed(2)));
};

const toCategoryName = (value) => {
  const normalized = normalizeText(value);
  return normalized ? toTitleCase(normalized) : 'Uncategorized';
};

const normalizeBusinessDisplayName = (value) => {
  const cleaned = normalizeText(value)
    .replace(/\s*\([^)]*@[^)]*\)\s*$/, '')
    .replace(/'s\s*Business$/i, '')
    .trim();

  return cleaned || '';
};

const toFileSafeName = (value) => {
  const cleaned = normalizeText(value)
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, '_');

  return cleaned || 'Recipe_Book';
};

const fitTextToWidth = (pdf, value, maxWidth) => {
  const text = normalizeText(value);
  if (!text) return '';

  if (pdf.getTextWidth(text) <= maxWidth) {
    return text;
  }

  let trimmed = text;
  while (trimmed.length > 1 && pdf.getTextWidth(`${trimmed}...`) > maxWidth) {
    trimmed = trimmed.slice(0, -1);
  }

  return `${trimmed}...`;
};

/* Business 173 (Kanmani Cafe) asked for a different closing layout: every
   complimentary item pooled into ONE section at the end of the book — the way
   Saved Preparations already are — and both of those closing sections indexed
   in the Table of Contents alongside the menu items. Every other business keeps
   the per-category "Complimentary Details" sections and the recipe-only
   contents page, so the flag is opt-in per business id. */
const POOLED_CLOSING_SECTION_BUSINESS_IDS = new Set(['173']);

const usesPooledClosingSections = (businessId) =>
  POOLED_CLOSING_SECTION_BUSINESS_IDS.has(normalizeText(businessId));

const getResolvedBusinessId = (businessId) => {
  const explicit = normalizeText(businessId);
  if (explicit) return explicit;

  if (typeof window === 'undefined') return '';
  return normalizeText(window.localStorage.getItem('businessId'));
};

/* The exported recipes carry the name of the business they were fetched for, so
   they are the one local source that is guaranteed to describe the business on
   the cover. Everything else in localStorage is shared across accounts and is
   only usable once businessIdentity has checked it against the id. */
const getBusinessDisplayNameFromLocal = (recipes, resolvedBusinessId) => {
  const candidates = [];

  if (Array.isArray(recipes) && recipes.length > 0) {
    const firstRecipe = recipes.find(Boolean) || {};
    candidates.push(
      firstRecipe.business_name,
      firstRecipe.businessName,
      firstRecipe.restaurant_name,
      firstRecipe.restaurantName,
      firstRecipe.business?.businessName,
      firstRecipe.business?.business_name,
      firstRecipe.business?.restaurantName,
      firstRecipe.business?.restaurant_name
    );
  }

  candidates.push(getCachedBusinessName(resolvedBusinessId));

  const found = candidates
    .map(normalizeBusinessDisplayName)
    .find(Boolean);

  if (found) return found;
  return '';
};

const resolveBusinessDisplayName = async (recipes, resolvedBusinessId) => {
  const localName = getBusinessDisplayNameFromLocal(recipes, resolvedBusinessId);
  if (localName) return localName;

  if (typeof window === 'undefined') return `Business ${resolvedBusinessId}`;

  try {
    const token = window.localStorage.getItem('token') || window.localStorage.getItem('authToken');
    if (!token) return `Business ${resolvedBusinessId}`;

    const envBase = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
    const normalizedBase = String(envBase || '').trim().replace(/\/+$/, '');
    const apiBase = normalizedBase
      ? (normalizedBase.endsWith('/api') ? normalizedBase : `${normalizedBase}/api`)
      : '/api';

    // Businesses.name for the business this book is actually being exported
    // for. This is the only endpoint keyed by business id, so it is asked
    // first — /auth/status and /auth/user below describe whichever business
    // the session is signed in as, which is not necessarily this one.
    // (Route is mounted at /api/business, singular.)
    try {
      const businessResp = await fetch(`${apiBase}/business/${encodeURIComponent(resolvedBusinessId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      if (businessResp && businessResp.ok) {
        const bdata = await businessResp.json();
        const bCandidate = normalizeBusinessDisplayName(
          bdata?.name || bdata?.businessName || bdata?.business_name || bdata?.restaurantName || bdata?.restaurant_name
        );
        if (bCandidate) return bCandidate;
      }
    } catch (_err) {
      // ignore and try the session endpoints next
    }

    // Try auth status for cached user/business info
    try {
      const response = await fetch(`${apiBase}/auth/status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (response && response.ok) {
        const data = await response.json();
        const candidate = normalizeBusinessDisplayName(
          data?.user?.businessName
          || data?.user?.business_name
          || data?.user?.restaurantName
          || data?.user?.restaurant_name
        );
        if (candidate) return candidate;
      }
    } catch (_err) {
      // ignore and try the user profile endpoint next
    }

    // Try full user profile endpoint, which includes the business name.
    try {
      const userResponse = await fetch(`${apiBase}/auth/user`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (userResponse && userResponse.ok) {
        const userData = await userResponse.json();
        const userCandidate = normalizeBusinessDisplayName(
          userData?.business?.businessName
          || userData?.business?.business_name
          || userData?.business?.restaurantName
          || userData?.business?.restaurant_name
          || userData?.businessName
          || userData?.restaurantName
        );
        if (userCandidate) return userCandidate;
      }
    } catch (_err) {
      // ignore
    }

    return `Business ${resolvedBusinessId}`;
  } catch (_error) {
    return `Business ${resolvedBusinessId}`;
  }
};

const readPreparationReviewDrafts = (businessId) => {
  if (typeof window === 'undefined') return [];

  const storageKey = `preparationReviewDrafts_${businessId}`;

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return [];

    return Object.entries(parsed)
      .map(([draftKey, draft]) => {
        if (!draft || typeof draft !== 'object') return null;

        const ingredients = Array.isArray(draft.ingredients)
          ? draft.ingredients
              .map((ingredient) => {
                const name = normalizeText(ingredient?.item_name || ingredient?.name || ingredient?.ingredient_name);
                const quantity = Number(ingredient?.quantity);
                const rawUnit = normalizeText(ingredient?.unit_symbol || ingredient?.unit_name || ingredient?.unit || '');
                const cleanUnit = rawUnit.replace(/\/+$/, '').trim();

                if (!name || !Number.isFinite(quantity) || quantity < 0) return null;

                return { name, quantity, unit: cleanUnit || '-' };
              })
              .filter(Boolean)
          : [];

        return {
          draftKey,
          preparationName: normalizeText(draft.preparationName) || 'Untitled Preparation',
          totalQuantity: Number(draft.totalQuantity) || 0,
          totalUnit: normalizeText(draft.selectedUnitSymbol || draft.selectedUnitName),
          ingredients,
          updatedAt: Number(draft.savedAt) || 0,
          isUnsaved: Boolean(draft.isUnsaved)
        };
      })
      .filter(Boolean)
      .sort((a, b) => (Number(b.updatedAt) || 0) - (Number(a.updatedAt) || 0));
  } catch (error) {
    console.warn('Failed to parse preparation review drafts:', error);
    return [];
  }
};

const loadSavedPreparationTemplates = async (businessId) => {
  if (typeof window === 'undefined') return [];

  try {
    const token = window.localStorage.getItem('token') || window.localStorage.getItem('authToken');
    const apiUrl = process.env.REACT_APP_API_URL || 'https://be.invexis.co.in/api';

    const response = await fetch(`${apiUrl}/medium-features/decomposition/templates?businessId=${businessId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined
    });

    if (!response.ok) return [];

    const result = await response.json();
    return Array.isArray(result?.personalized) ? result.personalized : [];
  } catch (error) {
    console.warn('Failed to load saved preparations for export:', error);
    return [];
  }
};

// Saved preparations are reachable from a recipe two ways:
//  • by inventory item — PersonalizedDecompositions.composite_item_id points at
//    the InventoryItems row the recipe consumes;
//  • by name — the preparation's composite_name equals the ingredient's name.
// composite_item_id is the authoritative link but is not populated for every
// template, and where it IS set the linked item can be named differently
// ("SMASH CHICKEN PATTY" stored against inventory item "CHICKEN PATTY"), so a
// name-only lookup silently drops those mappings. Index both.
const buildSavedPreparationLookup = (preparations) => {
  const byName = new Map();
  const byItemId = new Map();

  (preparations || []).forEach((prep) => {
    const key = normalizeKey(prep?.composite_name || prep?.name || prep?.preparationName);
    if (key) byName.set(key, prep);

    const itemId = normalizeText(prep?.composite_item_id ?? prep?.compositeItemId);
    if (itemId) byItemId.set(itemId, prep);
  });

  return { byName, byItemId };
};

const isEmptySavedPreparationLookup = (lookup) =>
  !lookup
  || !(lookup.byName instanceof Map)
  || (lookup.byName.size === 0 && (!(lookup.byItemId instanceof Map) || lookup.byItemId.size === 0));

// Resolve a single recipe ingredient to its saved-preparation template, if any.
const findSavedPreparationForIngredient = (ingredient, lookup) => {
  const itemId = normalizeText(ingredient?.itemId);
  if (itemId && lookup?.byItemId instanceof Map) {
    const byId = lookup.byItemId.get(itemId);
    if (byId) return byId;
  }

  const name = normalizeKey(ingredient?.name);
  if (name && lookup?.byName instanceof Map) {
    return lookup.byName.get(name) || null;
  }

  return null;
};

const resolveRecipeId = (recipe) => {
  const candidateIds = [
    recipe?.menu_item_id,
    recipe?.menuItemId,
    recipe?.id,
    recipe?.recipe_id
  ];

  for (const candidate of candidateIds) {
    const normalized = normalizeText(candidate);
    if (normalized) return normalized;
  }

  return '';
};

const buildRecipeLookup = (recipes) => {
  const byId = new Map();

  (recipes || []).forEach((recipe) => {
    const recipeId = resolveRecipeId(recipe);
    if (!recipeId) return;
    byId.set(recipeId, recipe);
  });

  return byId;
};

// The inventory item a recipe row consumes. The export endpoint calls it
// inventory_id, the recipe/draft editors carry item_id (or a camelCase alias).
const resolveIngredientItemId = (ingredient) => {
  const candidates = [
    ingredient?.inventory_id,
    ingredient?.inventoryId,
    ingredient?.item_id,
    ingredient?.itemId,
    ingredient?.atomic_item_id
  ];

  for (const candidate of candidates) {
    const normalized = normalizeText(candidate);
    if (normalized) return normalized;
  }

  return '';
};

const normalizeDraftIngredient = (ingredient) => {
  const name = normalizeText(ingredient?.name || ingredient?.ingredient_name || ingredient?.item_name);
  if (!name || normalizeKey(name) === 'new ingredient') return null;

  const qty100 = Number(ingredient?.qty100);
  const qty10 = Number(ingredient?.qty10);
  const fallbackQty = Number(ingredient?.quantity);

  let perPlate = 0;
  if (Number.isFinite(qty100) && qty100 >= 0) {
    perPlate = qty100 / 100;
  } else if (Number.isFinite(qty10) && qty10 >= 0) {
    perPlate = qty10 / 10;
  } else if (Number.isFinite(fallbackQty) && fallbackQty >= 0) {
    perPlate = fallbackQty;
  }

  if (!Number.isFinite(perPlate) || perPlate < 0) return null;

  const rawUnit = normalizeText(ingredient?.unit || ingredient?.unit_symbol || ingredient?.unit_name || '');
  const cleanUnit = rawUnit.replace(/\/+$/, '').trim();

  return {
    name,
    quantity: perPlate,
    unit: cleanUnit || '-',
    itemId: resolveIngredientItemId(ingredient)
  };
};

const normalizeServerIngredient = (ingredient) => {
  const name = normalizeText(ingredient?.ingredient_name || ingredient?.name || ingredient?.item_name);
  const quantity = Number(ingredient?.quantity);
  const rawUnit = normalizeText(ingredient?.unit || ingredient?.unit_symbol || ingredient?.unit_name || '');
  const cleanUnit = rawUnit.replace(/\/+$/, '').trim();

  if (!name || !Number.isFinite(quantity) || quantity < 0) return null;

  return { name, quantity, unit: cleanUnit || '-', itemId: resolveIngredientItemId(ingredient) };
};

const normalizeComplimentaryNames = (items) => {
  if (!Array.isArray(items)) return [];

  const names = items
    .map((entry) => {
      if (typeof entry === 'string') return normalizeText(entry);
      return normalizeText(entry?.name || entry?.item_name || entry?.complimentary_item_name);
    })
    .filter(Boolean);

  return Array.from(new Set(names));
};

const normalizeCustomizationNames = (items) => {
  if (!Array.isArray(items)) return [];

  const names = [];

  items.forEach((entry) => {
    if (typeof entry === 'string') {
      const name = normalizeText(entry);
      if (name) names.push(name);
      return;
    }

    if (!entry || typeof entry !== 'object') return;

    const directName = normalizeText(entry?.name || entry?.customization_name || entry?.label);
    if (directName && normalizeKey(directName) !== 'options') {
      names.push(directName);
    }

    if (Array.isArray(entry?.options)) {
      entry.options.forEach((option) => {
        const optionName = normalizeText(
          typeof option === 'string'
            ? option
            : option?.name || option?.label
        );

        if (optionName) names.push(optionName);
      });
    }
  });

  return Array.from(new Set(names));
};

const loadLinkedComplimentaryItems = async (recipeId) => {
  if (!recipeId) return [];

  try {
    const result = await FeaturesService.getLinkedComplimentaryItems(recipeId);
    if (result?.success && Array.isArray(result?.complimentaryItems)) {
      return result.complimentaryItems;
    }
    return [];
  } catch (error) {
    console.warn(`Failed to load linked complimentary items for recipe ${recipeId}:`, error);
    return [];
  }
};

const buildComplimentarySummary = (linkedItems, selectedNames = []) => {
  const linkedByName = new Map();

  (linkedItems || []).forEach((item) => {
    const name = normalizeText(item?.name || item?.item_name || item?.complimentary_item_name);
    if (!name) return;
    linkedByName.set(normalizeKey(name), item);
  });

  const combinedNames = Array.from(new Set([
    ...selectedNames,
    ...(linkedItems || []).map((item) => normalizeText(item?.name || item?.item_name || item?.complimentary_item_name))
  ].filter(Boolean)));

  return combinedNames.map((name) => {
    const linkedItem = linkedByName.get(normalizeKey(name));
    const ingredients = Array.isArray(linkedItem?.ingredients)
      ? linkedItem.ingredients
          .map((ingredient) => {
            const ingredientName = normalizeText(ingredient?.item_name || ingredient?.name);
            const quantity = Number(ingredient?.quantity);
            const rawUnit = normalizeText(ingredient?.unit_symbol || ingredient?.unit_name || ingredient?.unit || '');
            const cleanUnit = rawUnit.replace(/\/+$/, '').trim();

            if (!ingredientName || !Number.isFinite(quantity) || quantity < 0) return null;

            return { name: ingredientName, quantity, unit: cleanUnit || '-' };
          })
          .filter(Boolean)
      : [];

    const scalingType = normalizeText(linkedItem?.scaling_type).toUpperCase();
    const quantityMultiplier = Number(linkedItem?.quantity_multiplier);

    let scalingLabel = 'Association saved';
    if (scalingType === 'FIXED') {
      scalingLabel = `Fixed x${formatNumber(quantityMultiplier || 1)}`;
    } else if (scalingType === 'SCALED') {
      scalingLabel = `Scaled x${formatNumber(quantityMultiplier || 1)}`;
    }

    const yieldLabel = `${formatNumber(quantityMultiplier || 1)} Per Plate`;

    return {
      name,
      scalingLabel,
      yieldLabel,
      quantityMultiplier: Number.isFinite(quantityMultiplier) && quantityMultiplier > 0
        ? quantityMultiplier
        : 1,
      ingredients
    };
  });
};

const getWeightInGrams = (qty, unit) => {
  const u = String(unit || '').toLowerCase().trim();
  if (u === 'kg' || u === 'kgs' || u === 'kilogram' || u === 'kilograms') {
    return qty * 1000;
  }
  return qty;
};

const getVolumeInMl = (qty, unit) => {
  const u = String(unit || '').toLowerCase().trim();
  if (u === 'l' || u === 'lit' || u === 'liter' || u === 'litre' || u === 'liters' || u === 'litres') {
    return qty * 1000;
  }
  return qty;
};

const convertToBase = (qty, unit) => {
  const u = String(unit || '').toLowerCase().trim();
  if (['kg', 'kgs', 'kilogram', 'kilograms', 'g', 'gm', 'gms', 'gram', 'grams'].includes(u)) {
    return getWeightInGrams(qty, u);
  }
  if (['l', 'lit', 'liter', 'litre', 'liters', 'litres', 'ml', 'milliliter', 'milliliters'].includes(u)) {
    return getVolumeInMl(qty, u);
  }
  return qty;
};

const normalizeIngredientUnit = (qty, unit) => {
  let q = Number(qty);
  let u = String(unit || '').trim();
  const lowerUnit = u.toLowerCase();
  
  if (q > 0 && q < 1) {
    if (['kg', 'kgs', 'kilogram', 'kilograms'].includes(lowerUnit)) {
      q = q * 1000;
      u = 'g';
    } else if (['l', 'lit', 'liter', 'litre', 'liters', 'litres'].includes(lowerUnit)) {
      q = q * 1000;
      u = 'ml';
    }
  }
  return { quantity: q, unit: u };
};

const buildSavedPreparationSummary = (ingredients, savedPreparationLookup) => {
  if (!Array.isArray(ingredients) || ingredients.length === 0) return [];
  if (isEmptySavedPreparationLookup(savedPreparationLookup)) return [];

  return ingredients
    .map((ingredient) => {
      const sourceIngredientName = normalizeText(ingredient?.name);
      if (!sourceIngredientName) return null;

      const prepMeta = findSavedPreparationForIngredient(ingredient, savedPreparationLookup);
      if (!prepMeta) return null;

      // Display the preparation's own name — where composite_item_id links a
      // differently named inventory item, the template name is the one the
      // closing "Saved Preparations" section (and its page reference) uses.
      const preparationName = normalizeText(
        prepMeta?.composite_name || prepMeta?.name || prepMeta?.preparationName
      ) || sourceIngredientName;

      const usedQuantity = Number(ingredient?.quantity);
      const usedUnit = normalizeText(ingredient?.unit);
      const totalQuantity = Number(prepMeta?.total_weight ?? prepMeta?.totalQuantity);
      const totalUnitSymbol = normalizeText(prepMeta?.total_weight_unit_symbol ?? prepMeta?.totalWeightUnitSymbol ?? prepMeta?.unit);

      // Convert to base units for division (e.g. grams or milliliters)
      const usedBase = convertToBase(usedQuantity, usedUnit);
      const totalBase = convertToBase(totalQuantity, totalUnitSymbol);

      const scaleFactor = Number.isFinite(usedBase) && usedBase > 0 && Number.isFinite(totalBase) && totalBase > 0
        ? usedBase / totalBase
        : 1;

      const prepIngredients = Array.isArray(prepMeta?.components)
        ? prepMeta.components
            .map((component) => {
              const ingredientName = normalizeText(component?.item_name || component?.name || component?.ingredient_name);
              const baseQuantity = Number(component?.weight ?? component?.quantity);
              const rawUnit = normalizeText(component?.unit_symbol || component?.unit_name || component?.unit || '');
              const cleanUnit = rawUnit.replace(/\/+$/, '').trim();

              if (!ingredientName || !Number.isFinite(baseQuantity) || baseQuantity < 0) return null;

              const scaledQty = baseQuantity * scaleFactor;
              const normalized = normalizeIngredientUnit(scaledQty, cleanUnit);

              return {
                name: ingredientName,
                quantity: normalized.quantity,
                unit: normalized.unit || '-'
              };
            })
            .filter(Boolean)
        : [];

      return {
        preparationName,
        // The recipe row this preparation was resolved from — used to keep the
        // same line out of the raw-ingredient list when the two names differ.
        sourceIngredientName,
        sourceItemId: normalizeText(ingredient?.itemId),
        usedQuantity: Number.isFinite(usedQuantity) ? usedQuantity : 0,
        usedUnit,
        totalQuantity: Number.isFinite(totalQuantity) ? totalQuantity : 0,
        totalUnit: totalUnitSymbol,
        ingredients: prepIngredients
      };
    })
    .filter(Boolean);
};

const createTableTheme = () => ({
  theme: 'grid',
  headStyles: {
    fillColor: [255, 255, 255],
    textColor: [0, 0, 0],
    fontSize: 9,
    fontStyle: 'bold'
  },
  bodyStyles: {
    fontSize: 9,
    textColor: [0, 0, 0],
    fillColor: [255, 255, 255]
  },
  styles: {
    cellPadding: 4,
    overflow: 'linebreak',
    lineColor: [170, 170, 170],
    lineWidth: 0.08
  },
  margin: { left: 20, right: 20 }
});

const drawPdfHeader = (pdf, title, subtitle) => {
  const pageWidth = pdf.internal.pageSize.getWidth();

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(18);
  pdf.setTextColor(0, 0, 0);
  pdf.text(title, pageWidth / 2, 16, { align: 'center' });

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(80, 80, 80);
  pdf.text(subtitle, pageWidth / 2, 22, { align: 'center' });

  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.3);
  pdf.line(14, 25, pageWidth - 14, 25);

  return 30;
};

const drawCoverPage = (pdf, businessName, totalItems) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const frameX = 14;
  const frameY = 14;
  const frameW = pageWidth - 28;
  const frameH = pageHeight - 28;
  const centerX = pageWidth / 2;
  const centerY = pageHeight / 2 - 2;

  // double frame to match the printed cover look
  pdf.setDrawColor(175, 175, 175);
  pdf.setLineWidth(0.35);
  pdf.rect(frameX, frameY, frameW, frameH);
  pdf.rect(frameX + 1.5, frameY + 1.5, frameW - 3, frameH - 3);

  // Title
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(32);
  pdf.setTextColor(0, 0, 0);
  const title = fitTextToWidth(pdf, normalizeText(businessName).toUpperCase(), pageWidth - 76);
  pdf.text(title || 'RECIPE LIBRARY', centerX, centerY - 18, { align: 'center' });

  // Subtitle and decorative rule
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);
  pdf.setCharSpace(0.8);
  pdf.text('RECIPE BOOK', centerX - 4, centerY + 1, { align: 'center' });
  pdf.setCharSpace(0);

  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.4);
  pdf.line(centerX - 13, centerY + 6, centerX + 9, centerY + 6);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7.5);
  pdf.setTextColor(0, 0, 0);
  pdf.text(`${totalItems} Menu Items`, centerX, centerY + 24, { align: 'center' });
};

// groupedCategories must be the SAME array drawTableOfContents receives —
// menu-item categories followed by any closing-section groups — or the
// pre-allocated contents pages will not match what is drawn onto them.
const simulateTableOfContentsPages = (groupedCategories) => {
  const pageHeight = 297;
  let tocPages = 1;
  let yPos = 34; // drawHeader height

  groupedCategories.forEach((group) => {
    const itemsCount = group.items.length;
    const estimatedBlockHeight = Math.max(24, (Math.ceil(itemsCount / 2) * 7) + 28);
    const maxPageHeight = pageHeight - 58;

    if (estimatedBlockHeight <= maxPageHeight && yPos + estimatedBlockHeight > pageHeight - 24) {
      tocPages++;
      yPos = 34;
    } else if (estimatedBlockHeight > maxPageHeight && yPos > pageHeight - 50) {
      tocPages++;
      yPos = 34;
    }
    yPos += 8;
    for (let i = 0; i < itemsCount; i += 2) {
      const rowOffset = (i === 0) ? 5 : 0;
      yPos += 7 + rowOffset;
      if (yPos > pageHeight - 36) {
        tocPages++;
        yPos = 34;
      }
    }
    yPos += 8;
  });

  return tocPages;
};

const simulateLayout = (groupedCategories, startPageNum) => {
  let currentPage = startPageNum;
  let yPos = 18;
  const pageHeight = 297;
  
  const recipePageRanges = new Map();

  groupedCategories.forEach((group) => {
    currentPage++; // Category starts on new page
    yPos = 18 + 16;

    group.items.forEach((entry) => {
      const estimatedHeight = estimateRecipeEntryHeight(entry);

      if (yPos + estimatedHeight > pageHeight - 12) {
        currentPage++;
        yPos = 18 + 16;
      }

      const startPage = currentPage;
      
      const ingredientRowsCount = buildRecipeRowPlan(entry).length;
      let entryY = yPos + 10 + 6;
      
      const prepTime = Number(entry?.prepTime ?? entry?.prep_time_minutes);
      const cookTime = Number(entry?.cookTime ?? entry?.cook_time_minutes);
      const servings = Number(entry?.servings ?? entry?.servings_per_batch);
      if ((Number.isFinite(prepTime) && prepTime > 0) || 
          (Number.isFinite(cookTime) && cookTime > 0) || 
          (Number.isFinite(servings) && servings > 0)) {
        entryY += 4;
      }

      for (let r = 0; r < ingredientRowsCount; r++) {
        if (entryY > pageHeight - 28) {
          currentPage++;
          entryY = 18;
        }
        entryY += 9;
      }
      
      const endPage = currentPage;
      yPos = entryY + 4;
      
      const recipeId = entry?.recipeId || resolveRecipeId(entry);
      if (recipeId) {
        recipePageRanges.set(normalizeText(recipeId), { startPage, endPage });
      }
    });
  });

  return { recipePageRanges, totalPages: currentPage };
};

// Simulate which page each unique saved-preparation name lands on in the
// "Saved Preparation Details" section (rendered by renderSavedPreparationDetailSections).
// Returns a Map: normalizedPrepName -> pageNumber
const simulateSavedPrepDetailPages = (allRecipeEntries, groupMode, startPageNum) => {
  const pageHeight = 297;
  const savedPrepPageMap = new Map();
  const groupedCategories = groupEntriesByCategory(allRecipeEntries, groupMode);

  let currentPage = startPageNum;

  groupedCategories.forEach((group) => {
    const blocks = [];
    const seenPreps = new Set();

    group.items.forEach((entry) => {
      const savedPreparations = Array.isArray(entry?.savedPreparations) ? entry.savedPreparations : [];
      savedPreparations.forEach((preparation) => {
        const ingredients = Array.isArray(preparation?.ingredients) ? preparation.ingredients : [];
        if (ingredients.length === 0) return;
        const prepNameNormalized = normalizeKey(preparation.preparationName);
        if (seenPreps.has(prepNameNormalized)) return;
        seenPreps.add(prepNameNormalized);
        const yieldLabel = preparation.totalQuantity > 0
          ? `${formatNumber(preparation.totalQuantity)} ${preparation.totalUnit || ''}`.trim()
          : '';
        blocks.push({
          preparationName: preparation.preparationName,
          ingredients,
          yieldLabel
        });
      });
    });

    if (blocks.length === 0) return;

    // New page for this category's saved-prep section
    currentPage++;
    let yPos = 18 + 16; // heading height

    blocks.forEach((prep) => {
      const ingredientRows = Math.max(1, prep.ingredients.length);
      const blockHeight = 18 + 10 + 6 + (ingredientRows * 9) + 6;

      if (yPos + blockHeight > pageHeight - 12) {
        currentPage++;
        yPos = 18 + 16;
      }

      const key = normalizeKey(prep.preparationName);
      if (key && !savedPrepPageMap.has(key)) {
        savedPrepPageMap.set(key, currentPage);
      }

      yPos += blockHeight + 4;
    });
  });

  return savedPrepPageMap;
};

// Simulate how many pages the "Complimentary Details" section consumes.
// Mirrors the exact per-block pagination logic of renderComplimentaryDetailSections.
// Returns the page number at the END of the complimentary section.
const simulateComplimentaryDetailPages = (allRecipeEntries, groupMode, startPageNum) => {
  const pageHeight = 297;
  let currentPage = startPageNum;
  const groupedCategories = groupEntriesByCategory(allRecipeEntries, groupMode);

  groupedCategories.forEach((group) => {
    const blocks = [];
    const seenComplimentaries = new Set();

    group.items.forEach((entry) => {
      const complimentaryItems = Array.isArray(entry?.complimentaryItems) ? entry.complimentaryItems : [];
      complimentaryItems.forEach((item) => {
        const ingredients = Array.isArray(item?.ingredients) ? item.ingredients : [];
        // Mirror rendering: always at least one row ("No ingredients mapped")
        const ingredientRows = ingredients.length > 0 ? ingredients.length : 1;
        const itemNameNormalized = normalizeKey(item?.name);
        if (!itemNameNormalized || seenComplimentaries.has(itemNameNormalized)) return;
        seenComplimentaries.add(itemNameNormalized);
        blocks.push({ ingredientRows });
      });
    });

    if (blocks.length === 0) return;

    // New page for this category's complimentary section (heading)
    currentPage++;
    let yPos = 18 + 16; // heading height

    blocks.forEach((block) => {
      // Mirror estimateRecipeEntryHeight for a syntheticEntry with no meta / saved preps:
      // estimate = 12 + (ingredientRows + 1) * 7 + 8
      const blockHeight = 12 + (block.ingredientRows + 1) * 7 + 8;
      if (yPos + blockHeight > pageHeight - 12) {
        currentPage++;
        yPos = 18 + 16;
      }
      yPos += blockHeight;
    });
  });

  return currentPage;
};

const drawItemWithDotLeaders = (pdf, nameText, pageText, xStart, xEnd, y) => {
  const dotWidth = pdf.getTextWidth('.');
  const nameWidth = pdf.getTextWidth(nameText);
  const pageWidthVal = pdf.getTextWidth(pageText);
  
  const padding = 2; // minimum spacing between text and dots
  
  pdf.text(nameText, xStart, y);
  pdf.text(pageText, xEnd, y, { align: 'right' });
  
  const dotsStartX = xStart + nameWidth + padding;
  const dotsEndX = xEnd - pageWidthVal - padding;
  const dotsWidth = dotsEndX - dotsStartX;
  
  if (dotsWidth > dotWidth) {
    const numDots = Math.floor(dotsWidth / dotWidth);
    const dotsStr = '.'.repeat(numDots);
    pdf.text(dotsStr, dotsStartX, y);
  }
};

// Closing-section groups ("Complimentary Items", "Saved Preparations") are listed
// after the menu-item categories. Their page numbers are only known once those
// sections have actually been rendered, so their rows are not drawn here — each
// row is recorded in deferredPageRefs and drawn later by drawDeferredTocRows.
const drawTableOfContents = (
  pdf,
  groupedCategories,
  recipePageRanges,
  tableTheme,
  { extraGroups = [], deferredPageRefs = null } = {}
) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const left = 14;
  const right = pageWidth - 14;
  let currentTocPage = 2; // Starts on Page 2

  // The contents pages are pre-allocated from simulateTableOfContentsPages.
  // Should a book ever need one more than estimated, append it rather than
  // drawing onto a page that does not exist.
  const goToTocPage = (pageNumber) => {
    const pageCount = pdf.getNumberOfPages();
    if (pageNumber > pageCount) {
      pdf.setPage(pageCount);
      pdf.addPage();
      return;
    }
    pdf.setPage(pageNumber);
  };

  const drawHeader = () => {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(25, 25, 25);
    pdf.text('TABLE OF CONTENTS', pageWidth / 2, 22, { align: 'center' });

    pdf.setDrawColor(60, 60, 60);
    pdf.setLineWidth(0.3);
    pdf.line(left, 26, right, 26);

    return 34;
  };

  let yPos = drawHeader();
  let globalIndex = 1;

  [...groupedCategories, ...extraGroups].forEach((group) => {
    // Closing-section groups number their rows from 1, matching the numbering
    // the rendered section itself uses.
    const isDeferredGroup = Boolean(group?.deferred);
    let deferredIndex = 1;
    const nextIndex = () => (isDeferredGroup ? deferredIndex++ : globalIndex++);

    const itemsCount = group.items.length;
    const estimatedBlockHeight = Math.max(24, (Math.ceil(itemsCount / 2) * 7) + 28);
    const maxPageHeight = pageHeight - 58;
    
    if (estimatedBlockHeight <= maxPageHeight && yPos + estimatedBlockHeight > pageHeight - 24) {
      currentTocPage++;
      goToTocPage(currentTocPage);
      yPos = drawHeader();
    } else if (estimatedBlockHeight > maxPageHeight && yPos > pageHeight - 50) {
      currentTocPage++;
      goToTocPage(currentTocPage);
      yPos = drawHeader();
    }

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(30, 30, 30);
    pdf.text(`${group.category} (${itemsCount})`, 20, yPos);
    yPos += 4;

    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.4);
    pdf.line(20, yPos, pageWidth - 20, yPos);
    yPos += 4;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(80, 80, 80);

    const leftColX = 22;
    const leftColEnd = Math.round(pageWidth / 2) - 8;
    const rightColX = Math.round(pageWidth / 2) + 8;
    const rightColEnd = pageWidth - 22;

    const leftMaxWidth = leftColEnd - leftColX;
    const rightMaxWidth = rightColEnd - rightColX;

    let itemIdx = 0;
    while (itemIdx < itemsCount) {
      // Calculate how many rows fit on the current page
      let rowsPerPage = Math.floor((pageHeight - 36 - yPos - 5) / 7) + 1;
      if (rowsPerPage < 1) {
        currentTocPage++;
        goToTocPage(currentTocPage);
        yPos = drawHeader();
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(80, 80, 80);
        rowsPerPage = Math.floor((pageHeight - 36 - yPos - 5) / 7) + 1;
      }

      const itemsToRender = Math.min(itemsCount - itemIdx, 2 * rowsPerPage);
      const pageItems = group.items.slice(itemIdx, itemIdx + itemsToRender);
      const leftCount = Math.ceil(pageItems.length / 2);

      const leftItems = pageItems.slice(0, leftCount);
      const rightItems = pageItems.slice(leftCount);

      const drawTocColumn = (columnItems, colX, colEnd, colMaxWidth) => {
        for (let r = 0; r < columnItems.length; r++) {
          const item = columnItems[r];
          const itemY = yPos + 5 + (r * 7);
          const nameText = `${nextIndex()}. ${toTitleCase(item?.recipeName || 'Recipe')}`;

          // Closing-section rows: the section has not been rendered yet, so the
          // page number is unknown. Record the row and draw it once it is.
          if (isDeferredGroup) {
            if (Array.isArray(deferredPageRefs)) {
              deferredPageRefs.push({
                tocPage: currentTocPage,
                x: colX,
                xEnd: colEnd,
                y: itemY,
                maxWidth: colMaxWidth,
                nameText,
                lookupKey: normalizeText(item?.lookupKey),
                pageMapKey: group.pageMapKey
              });
            }
            continue;
          }

          const range = recipePageRanges.get(normalizeText(item?.recipeId)) || { startPage: '-', endPage: '-' };
          const pageText = range.startPage === range.endPage 
            ? String(range.startPage) 
            : `${range.startPage}–${range.endPage}`;

          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(0, 0, 0);
          const truncatedName = fitTextToWidth(pdf, nameText, colMaxWidth - pdf.getTextWidth(pageText) - 6);
          drawItemWithDotLeaders(pdf, truncatedName, pageText, colX, colEnd, itemY);
        }
      };

      // Draw left column
      drawTocColumn(leftItems, leftColX, leftColEnd, leftMaxWidth);

      // Draw right column
      drawTocColumn(rightItems, rightColX, rightColEnd, rightMaxWidth);

      const maxRows = Math.max(leftItems.length, rightItems.length);
      yPos += (maxRows * 7) + (maxRows > 0 ? 5 : 0);

      itemIdx += itemsToRender;

      if (itemIdx < itemsCount) {
        currentTocPage++;
        goToTocPage(currentTocPage);
        yPos = drawHeader();
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(80, 80, 80);
      }
    }

    yPos += 8;
  });
};

// Contents groups for the pooled closing sections. Built from the very arrays
// the sections render, so a row's number in the contents always matches the
// number printed beside the item in its section.
const buildClosingSectionTocGroups = (complimentaryBlocks, savedPreparationBlocks) => {
  const groups = [];

  if (Array.isArray(complimentaryBlocks) && complimentaryBlocks.length > 0) {
    groups.push({
      category: COMPLIMENTARY_ITEMS_HEADING,
      deferred: true,
      pageMapKey: 'complimentary',
      items: complimentaryBlocks.map((block) => ({
        recipeName: block.recipeName,
        lookupKey: block.lookupKey || normalizeKey(block.recipeName)
      }))
    });
  }

  if (Array.isArray(savedPreparationBlocks) && savedPreparationBlocks.length > 0) {
    groups.push({
      category: SAVED_PREPARATIONS_SECTION_HEADING,
      deferred: true,
      pageMapKey: 'savedPreparations',
      items: savedPreparationBlocks.map((prep) => ({
        recipeName: toTitleCase(prep.preparationName),
        lookupKey: normalizeKey(prep.preparationName)
      }))
    });
  }

  return groups;
};

// Fill in the contents rows that were held back until their section had been
// rendered and its real page numbers were known.
const drawDeferredTocRows = (pdf, deferredPageRefs, pageMaps = {}) => {
  if (!Array.isArray(deferredPageRefs) || deferredPageRefs.length === 0) return;

  deferredPageRefs.forEach((ref) => {
    const pageMap = pageMaps[ref.pageMapKey];
    const resolvedPage = pageMap instanceof Map ? pageMap.get(ref.lookupKey) : undefined;
    const pageText = resolvedPage === undefined ? '-' : String(resolvedPage);

    pdf.setPage(ref.tocPage);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);

    const truncatedName = fitTextToWidth(pdf, ref.nameText, ref.maxWidth - pdf.getTextWidth(pageText) - 6);
    drawItemWithDotLeaders(pdf, truncatedName, pageText, ref.x, ref.xEnd, ref.y);
  });
};

const addSectionHeading = (pdf, yPos, heading) => {
  const pageHeight = pdf.internal.pageSize.getHeight();
  const pageWidth = pdf.internal.pageSize.getWidth();
  let nextY = yPos;

  if (nextY > pageHeight - 24) {
    pdf.addPage();
    nextY = 18;
  }

  // Centered uppercase section heading with thin rule
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(30, 30, 30);
  const headingY = nextY + 6;

  // thicker decorative rules above and below heading to match reference
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.9);
  pdf.line(24, headingY - 12, pageWidth - 24, headingY - 12);

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.setTextColor(30, 30, 30);
  pdf.text(String(heading).toUpperCase(), pageWidth / 2, headingY, { align: 'center' });

  // make the lower rule darker to match the strong divider in reference
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.9);
  pdf.line(24, headingY + 8, pageWidth - 24, headingY + 8);

  return headingY + 14;
};

const addPdfFooters = (pdf, options = {}) => {
  const skipPages = options.skipPages instanceof Set ? options.skipPages : new Set();

  const pageCount = (typeof pdf.getNumberOfPages === 'function')
    ? pdf.getNumberOfPages()
    : (pdf.internal && pdf.internal.pages ? Object.keys(pdf.internal.pages).length - 1 : 1);

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  for (let p = 1; p <= pageCount; p += 1) {
    if (skipPages.has(p)) continue;
    pdf.setPage(p);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(13);
    pdf.setTextColor(110, 110, 110);
    pdf.text(`Page ${p} of ${pageCount}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
    pdf.text('Generated by Invexis', 14, pageHeight - 8);
  }
};

// Resolve which bucket an entry belongs to for a given grouping mode.
// 'master' groups by the item's master category (falling back to its
// subcategory when no master category is assigned); any other value
// groups by subcategory — the original/default behaviour.
const resolveEntryGroupName = (entry, groupMode = 'sub') => {
  if (groupMode === 'master') {
    const master = normalizeText(entry?.masterCategory);
    if (master) return toTitleCase(master);
  }
  return toCategoryName(entry?.category);
};

const groupEntriesByCategory = (entries, groupMode = 'sub') => {
  const grouped = new Map();

  (entries || []).forEach((entry) => {
    const category = resolveEntryGroupName(entry, groupMode);
    if (!grouped.has(category)) {
      grouped.set(category, []);
    }
    grouped.get(category).push(entry);
  });

  return Array.from(grouped.entries())
    .map(([category, items]) => ({
      category,
      items: [...items].sort((a, b) =>
        normalizeText(a?.recipeName).localeCompare(normalizeText(b?.recipeName), undefined, { sensitivity: 'base' })
      )
    }))
    .sort((a, b) => a.category.localeCompare(b.category, undefined, { sensitivity: 'base' }));
};

const getSavedPreparationRowCount = (entry) => {
  const savedPreparations = Array.isArray(entry?.savedPreparations) ? entry.savedPreparations : [];

  return savedPreparations.reduce((count, prep) => {
    const ingredientCount = Array.isArray(prep?.ingredients) ? prep.ingredients.length : 0;
    return count + Math.max(1, ingredientCount);
  }, 0);
};

const RAW_INGREDIENTS_LABEL = 'RAW INGREDIENTS';
const SAVED_PREPARATIONS_LABEL = 'SAVED PREPARATIONS';

const toExportQuantity = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : '';
};

// A complimentary item is a sub-recipe, so it has no single native quantity of its
// own — its components and their amounts are listed in the Complimentary Details
// section. What the link itself measures is how many portions accompany one plate,
// so that is what the recipe row reports.
const summariseComplimentaryQuantity = (item) => {
  const multiplier = Number(item?.quantityMultiplier);
  const perPlate = Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 1;

  return {
    quantity: perPlate,
    unit: perPlate === 1 ? 'serving' : 'servings'
  };
};

// Single source of truth for what an item contributes to the Recipe Book, split
// the way both exports present it:
//   • rawIngredients      — recipe rows that are plain inventory ingredients
//   • savedPreparations   — recipe rows that resolve to a saved preparation,
//                           carrying the quantity + unit consumed by the item
//   • complimentaryOnly   — linked complimentary items with no recipe row
// A row resolved to a saved preparation is reported ONLY under savedPreparations
// so the same quantity is never listed twice.
const buildRecipeExportSections = (entry) => {
  const ingredients = Array.isArray(entry?.ingredients) ? entry.ingredients : [];
  const savedPreparations = Array.isArray(entry?.savedPreparations) ? entry.savedPreparations : [];
  const complimentaryItems = Array.isArray(entry?.complimentaryItems) ? entry.complimentaryItems : [];

  // Keyed on the recipe row each preparation was resolved from — the preparation's
  // own name can differ when the link came from composite_item_id.
  const preparationRowKeys = new Set(
    savedPreparations
      .map((prep) => normalizeKey(prep?.sourceIngredientName || prep?.preparationName))
      .filter(Boolean)
  );
  const preparationItemIds = new Set(
    savedPreparations.map((prep) => normalizeText(prep?.sourceItemId)).filter(Boolean)
  );

  const complimentaryNameSet = new Set(
    complimentaryItems.map((item) => normalizeKey(item?.name)).filter(Boolean)
  );

  const seenNames = new Set();
  const rawIngredients = [];

  ingredients.forEach((ingredient) => {
    const rawName = normalizeText(ingredient?.name || 'Ingredient');
    const key = normalizeKey(rawName);
    const itemId = normalizeText(ingredient?.itemId);

    if ((key && preparationRowKeys.has(key)) || (itemId && preparationItemIds.has(itemId))) return;

    if (key) seenNames.add(key);

    const isComplimentary = Boolean(key) && complimentaryNameSet.has(key);

    rawIngredients.push({
      type: isComplimentary ? 'Complimentary' : 'Ingredient',
      name: toTitleCase(rawName),
      displayName: isComplimentary ? `${toTitleCase(rawName)} (Comp)` : toTitleCase(rawName),
      quantity: toExportQuantity(ingredient?.quantity),
      unit: normalizeText(ingredient?.unit) || '-'
    });
  });

  // Linked complimentary items that never appear as a recipe row still belong in
  // the list — quantities live in the Complimentary Details section.
  complimentaryItems.forEach((item) => {
    const compName = normalizeText(item?.name);
    const key = normalizeKey(compName);
    if (!key || seenNames.has(key)) return;
    seenNames.add(key);

    const compAmount = summariseComplimentaryQuantity(item);

    rawIngredients.push({
      type: 'Complimentary',
      name: toTitleCase(compName || 'Complimentary Item'),
      displayName: `${toTitleCase(compName || 'Complimentary Item')} (Comp)`,
      quantity: toExportQuantity(compAmount.quantity),
      unit: compAmount.unit
    });
  });

  const preparations = savedPreparations.map((prep) => {
    const prepName = normalizeText(prep?.preparationName) || 'Saved Preparation';

    return {
      type: 'Preparation',
      name: toTitleCase(prepName),
      key: normalizeKey(prepName),
      quantity: toExportQuantity(prep?.usedQuantity),
      unit: normalizeText(prep?.usedUnit) || '-',
      componentCount: Array.isArray(prep?.ingredients) ? prep.ingredients.length : 0
    };
  });

  return { rawIngredients, savedPreparations: preparations };
};

// Flat, printable row plan for one PDF entry. Shared by the renderer, the height
// estimator and the page-number simulation so all three always agree on how many
// 9mm rows an entry occupies.
const buildRecipeRowPlan = (entry) => {
  const { rawIngredients, savedPreparations } = buildRecipeExportSections(entry);

  // Complimentary / saved-preparation detail blocks reuse this renderer for a
  // plain ingredient list; they opt out of the section headings.
  const sectioned = entry?.sectioned !== false;
  const rows = [];

  if (sectioned) {
    rows.push({ kind: 'section', label: RAW_INGREDIENTS_LABEL });
  }

  if (rawIngredients.length === 0) {
    rows.push({
      kind: 'empty',
      name: sectioned ? 'No raw ingredients saved' : 'No ingredients saved'
    });
  } else {
    rawIngredients.forEach((line, index) => {
      rows.push({
        kind: 'item',
        serial: index + 1,
        name: line.displayName,
        quantity: line.quantity === '' ? '-' : formatNumber(line.quantity),
        unit: line.unit
      });
    });
  }

  if (savedPreparations.length > 0) {
    if (sectioned) {
      rows.push({ kind: 'section', label: SAVED_PREPARATIONS_LABEL });
    }

    savedPreparations.forEach((prep, index) => {
      rows.push({
        kind: 'item',
        serial: index + 1,
        name: sectioned ? prep.name : `${prep.name} (Saved Prep)`,
        quantity: prep.quantity === '' || prep.quantity === 0 ? '-' : formatNumber(prep.quantity),
        unit: prep.unit,
        savedPrepKey: prep.key
      });
    });
  }

  return rows;
};

const estimateRecipeEntryHeight = (entry) => {
  const ingredientRows = Math.max(1, buildRecipeRowPlan(entry).length);

  let estimate = 12;
  estimate += (ingredientRows + 1) * 7;

  const prepTime = Number(entry?.prepTime ?? entry?.prep_time_minutes);
  const cookTime = Number(entry?.cookTime ?? entry?.cook_time_minutes);
  const servings = Number(entry?.servings ?? entry?.servings_per_batch);
  if ((Number.isFinite(prepTime) && prepTime > 0) || 
      (Number.isFinite(cookTime) && cookTime > 0) || 
      (Number.isFinite(servings) && servings > 0)) {
    estimate += 4;
  }

  return estimate + 8;
};

const renderRecipeEntry = (pdf, startY, entry, tableTheme, itemIndex, pendingAnnotations) => {
  let yPos = startY;

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const leftX = 22;
  const rightX = pdf.internal.pageSize.getWidth() - 22;
  const colSerialX = leftX;
  const colNameX = leftX + 12;
  const colQtyX = rightX - 50;
  const colUnitX = rightX - 18;

  // Title and optional price on right
  const ingredientRows = buildRecipeRowPlan(entry);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(0, 0, 0);
  
  let titleText = `${itemIndex}. ${toTitleCase(entry?.recipeName || 'Recipe')}`;
  if (entry?.isSavedPrepDetail && entry?.yieldLabel) {
    titleText += ` (batch prepared ${entry.yieldLabel})`;
  }
  // ensure title doesn't collide with the column headers below
  pdf.text(titleText, 20, yPos + 4);

  // Render either the preparation batch yield or the recipe menu price right-aligned
  if (entry?.yieldLabel && !entry?.isSavedPrepDetail) {
    pdf.setCharSpace(0);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text(entry.yieldLabel, pageWidth - 20, yPos + 4, { align: 'right' });
  } else if (!entry?.isSavedPrepDetail) {
    const priceCandidate = entry?.menuPrice ?? entry?.price ?? entry?.salePrice ?? entry?.sellingPrice ?? entry?.menu_price;
    const numericPrice = Number(priceCandidate);
    if (Number.isFinite(numericPrice)) {
      const priceText = `Rs ${numericPrice.toFixed(2)}`; // use ASCII 'Rs' to avoid font/encoding issues
      pdf.setCharSpace(0);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text(priceText, pageWidth - 20, yPos + 4, { align: 'right' });
    }
  }

  // Metadata row (Prep Time, Cook Time, Servings)
  const metadataParts = [];
  const prepTime = Number(entry?.prepTime ?? entry?.prep_time_minutes);
  const cookTime = Number(entry?.cookTime ?? entry?.cook_time_minutes);
  const servings = Number(entry?.servings ?? entry?.servings_per_batch);

  if (Number.isFinite(prepTime) && prepTime > 0) {
    metadataParts.push(`Prep: ${prepTime} mins`);
  }
  if (Number.isFinite(cookTime) && cookTime > 0) {
    metadataParts.push(`Cook: ${cookTime} mins`);
  }
  if (Number.isFinite(servings) && servings > 0) {
    metadataParts.push(`Yield: ${servings} Portions`);
  }

  const metadataText = metadataParts.join('  |  ');
  if (metadataText) {
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(8.5);
    pdf.setTextColor(100, 100, 100);
    pdf.text(metadataText, 20, yPos + 8);
    yPos += 4;
  }

  // Move down to make space for headers, then draw column headers: # | INGREDIENT | QTY | UNIT
  yPos += 10;
  const headerFontSize = 9;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(headerFontSize);
  pdf.setTextColor(0, 0, 0);
  pdf.text('S.No', colSerialX, yPos);
  pdf.text('INGREDIENT', colNameX, yPos);
  pdf.text('QTY', colQtyX, yPos, { align: 'right' });
  pdf.text('UNIT', colUnitX, yPos, { align: 'right' });

  // thin separator under headers
  yPos += 6;
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.4);
  pdf.line(leftX, yPos - 3, rightX, yPos - 3);

  pdf.setFontSize(10);
  ingredientRows.forEach((row) => {
    if (yPos > pageHeight - 28) {
      pdf.addPage();
      yPos = 18;
    }

    // Section divider: "RAW INGREDIENTS" / "SAVED PREPARATIONS"
    if (row.kind === 'section') {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8.5);
      pdf.setTextColor(70, 70, 70);
      pdf.setCharSpace(0.4);
      pdf.text(String(row.label || '').toUpperCase(), colSerialX, yPos + 4);
      pdf.setCharSpace(0);
      pdf.setFontSize(10);

      yPos += 9;

      pdf.setDrawColor(150, 150, 150);
      pdf.setLineWidth(0.3);
      pdf.line(leftX, yPos - 3, rightX, yPos - 3);
      return;
    }

    if (row.kind === 'empty') {
      pdf.setFont('helvetica', 'italic');
      pdf.setFontSize(10);
      pdf.setTextColor(120, 120, 120);
      pdf.text(String(row.name || ''), colNameX, yPos + 4);

      yPos += 9;

      pdf.setDrawColor(220, 220, 220);
      pdf.setLineWidth(0.2);
      pdf.line(leftX, yPos - 3, rightX, yPos - 3);
      return;
    }

    const serial = String(row.serial ?? '');
    const name = String(row.name || '');
    const qty = String(row.quantity || '');
    const unit = String(row.unit || '');

    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(80, 80, 80);
    pdf.text(serial, colSerialX, yPos + 4);

    // Render the ingredient name as-is (no page number yet)
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text(name, colNameX, yPos + 4);

    // For Saved Prep rows: record position so we can append the page number later
    if (row.savedPrepKey && Array.isArray(pendingAnnotations)) {
      // Calculate x position right after the rendered name text
      const nameWidth = pdf.getTextWidth(name);
      pendingAnnotations.push({
        page: pdf.internal.getCurrentPageInfo().pageNumber,
        x: colNameX + nameWidth,
        y: yPos + 4,
        baseName: row.savedPrepKey
      });
    }

    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(qty, colQtyX, yPos + 4, { align: 'right' });

    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(unit, colUnitX, yPos + 4, { align: 'right' });

    yPos += 9;

    pdf.setDrawColor(220, 220, 220);
    pdf.setLineWidth(0.2);
    pdf.line(leftX, yPos - 3, rightX, yPos - 3);
  });

  return yPos + 4;
};

const renderRecipesByCategory = (
  pdf,
  entries,
  sectionLabel,
  tableTheme,
  { showSectionPrefix = true, groupMode = 'sub' } = {},
  pendingAnnotations = null
) => {
  const groupedCategories = groupEntriesByCategory(entries, groupMode);
  const pageHeight = pdf.internal.pageSize.getHeight();

  groupedCategories.forEach((group) => {
    const headingText = (showSectionPrefix && sectionLabel)
      ? `${sectionLabel} - ${group.category}`
      : group.category;

    pdf.addPage();
    let yPos = 18;
    yPos = addSectionHeading(pdf, yPos, headingText);

    group.items.forEach((entry, index) => {
      const estimatedHeight = estimateRecipeEntryHeight(entry);

      if (yPos + estimatedHeight > pageHeight - 12) {
        pdf.addPage();
        yPos = 18;
        yPos = addSectionHeading(pdf, yPos, headingText);
      }

      yPos = renderRecipeEntry(pdf, yPos, entry, tableTheme, index + 1, pendingAnnotations);
    });
  });
};

const formatSavedPreparationIngredientName = (ingredient) => {
  const rawName = normalizeText(ingredient?.name || 'Ingredient');
  const cleanedName = rawName.replace(/\s*\(Saved Prep\)\s*$/i, '');

  return toTitleCase(cleanedName || 'Ingredient');
};

const estimateSavedPreparationBlockHeight = (prep) => {
  const ingredients = Array.isArray(prep?.ingredients) ? prep.ingredients : [];
  const ingredientRows = Math.max(1, ingredients.length);

  return 18 + 10 + 6 + (ingredientRows * 9) + 6;
};

const renderSavedPreparationBlock = (pdf, startY, prep, itemIndex) => {
  let yPos = startY;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const leftX = 22;
  const rightX = pageWidth - 22;
  const colSerialX = leftX;
  const colNameX = leftX + 12;
  const colQtyX = rightX - 50;
  const colUnitX = rightX - 18;

  const ingredients = Array.isArray(prep?.ingredients) ? prep.ingredients : [];
  const rows = ingredients.length > 0
    ? ingredients.map((ingredient) => ([
        formatSavedPreparationIngredientName(ingredient),
        formatNumber(ingredient?.quantity),
        normalizeText(ingredient?.unit) || '-'
      ]))
    : [['No ingredients saved', '-', '-']];

  const yieldLabel = prep?.totalQuantity > 0
    ? `${formatNumber(prep.totalQuantity)} ${prep.totalUnit || ''}`.trim()
    : '';

  const sourceLabel = prep?.isUnsaved ? 'Local draft' : 'Existing preparation';
  const subTitle = [yieldLabel, sourceLabel].filter(Boolean).join(' | ');

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(0, 0, 0);
  pdf.text(`${itemIndex}. ${toTitleCase(prep?.preparationName || 'Saved Preparation')}`, 14, yPos + 4);

  if (subTitle) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(80, 80, 80);
    pdf.text(subTitle, 14, yPos + 8);
  }

  yPos += 10;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);
  pdf.text('S.No', colSerialX, yPos);
  pdf.text('INGREDIENT', colNameX, yPos);
  pdf.text('QTY', colQtyX, yPos, { align: 'right' });
  pdf.text('UNIT', colUnitX, yPos, { align: 'right' });

  yPos += 6;
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.4);
  pdf.line(leftX, yPos - 3, rightX, yPos - 3);

  rows.forEach((row, rowIndex) => {
    if (yPos > pageHeight - 28) {
      pdf.addPage();
      yPos = 18;
    }

    const name = String(row[0] || '');
    const qty = String(row[1] || '');
    const unit = String(row[2] || '');

    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(80, 80, 80);
    pdf.text(String(rowIndex + 1), colSerialX, yPos + 4);

    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);
    pdf.text(name, colNameX, yPos + 4);

    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(qty, colQtyX, yPos + 4, { align: 'right' });

    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(unit, colUnitX, yPos + 4, { align: 'right' });

    yPos += 9;

    pdf.setDrawColor(220, 220, 220);
    pdf.setLineWidth(0.2);
    pdf.line(leftX, yPos - 3, rightX, yPos - 3);
  });

  return yPos + 4;
};

// Shape a decomposition template (from /medium-features/decomposition/templates)
// into the {preparationName, totalQuantity, totalUnit, ingredients} form the
// saved-preparation renderers consume. Components keep their RAW batch weights.
const normalizeSavedPreparationTemplate = (template) => {
  const name = normalizeText(template?.composite_name || template?.name || template?.preparationName);
  if (!name) return null;

  const totalQuantity = Number(template?.total_weight ?? template?.totalQuantity) || 0;
  const totalUnit = normalizeText(template?.total_weight_unit_symbol ?? template?.total_weight_unit_name ?? template?.unit);

  const ingredients = Array.isArray(template?.components)
    ? template.components
        .map((component) => {
          const componentName = normalizeText(component?.item_name || component?.name || component?.ingredient_name);
          const quantity = Number(component?.weight ?? component?.quantity);
          const rawUnit = normalizeText(component?.unit_symbol || component?.unit_name || component?.unit || '');
          const cleanUnit = rawUnit.replace(/\/+$/, '').trim();

          if (!componentName || !Number.isFinite(quantity) || quantity < 0) return null;

          return { name: componentName, quantity, unit: cleanUnit || '-' };
        })
        .filter(Boolean)
    : [];

  return { preparationName: name, totalQuantity, totalUnit, ingredients };
};

// One block in the bulk "Saved Preparations" section: prep name with the batch
// yield right-aligned, then the raw component rows exactly as stored in the DB.
const renderSavedPrepTemplateBlock = (pdf, startY, prep, itemIndex) => {
  let yPos = startY;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const leftX = 22;
  const rightX = pageWidth - 22;
  const colSerialX = leftX;
  const colNameX = leftX + 12;
  const colQtyX = rightX - 50;
  const colUnitX = rightX - 18;

  const ingredients = Array.isArray(prep?.ingredients) ? prep.ingredients : [];
  const rows = ingredients.length > 0
    ? ingredients.map((ingredient) => ([
        toTitleCase(ingredient?.name || 'Ingredient'),
        formatNumber(ingredient?.quantity),
        normalizeText(ingredient?.unit) || '-'
      ]))
    : [['No ingredients saved', '-', '-']];

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(0, 0, 0);
  pdf.text(`${itemIndex}. ${toTitleCase(prep?.preparationName || 'Saved Preparation')}`, 20, yPos + 4);

  if (Number(prep?.totalQuantity) > 0) {
    const batchLabel = `Batch - ${formatNumber(prep.totalQuantity)} ${prep.totalUnit || ''}`.trim();
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text(batchLabel, pageWidth - 20, yPos + 4, { align: 'right' });
  }

  yPos += 10;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);
  pdf.text('S.No', colSerialX, yPos);
  pdf.text('INGREDIENT', colNameX, yPos);
  pdf.text('QTY', colQtyX, yPos, { align: 'right' });
  pdf.text('UNIT', colUnitX, yPos, { align: 'right' });

  yPos += 6;
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.4);
  pdf.line(leftX, yPos - 3, rightX, yPos - 3);

  rows.forEach((row, rowIndex) => {
    if (yPos > pageHeight - 28) {
      pdf.addPage();
      yPos = 18;
    }

    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(80, 80, 80);
    pdf.text(String(rowIndex + 1), colSerialX, yPos + 4);

    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);
    pdf.text(String(row[0] || ''), colNameX, yPos + 4);

    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(String(row[1] || ''), colQtyX, yPos + 4, { align: 'right' });

    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(String(row[2] || ''), colUnitX, yPos + 4, { align: 'right' });

    yPos += 9;

    pdf.setDrawColor(220, 220, 220);
    pdf.setLineWidth(0.2);
    pdf.line(leftX, yPos - 3, rightX, yPos - 3);
  });

  return yPos + 4;
};

const SAVED_PREPARATIONS_SECTION_HEADING = 'Saved Preparations';

// The templates the bulk "Saved Preparations" section renders, in the exact
// order it renders them. Shared with the Table of Contents so both agree on
// order and numbering.
const collectBulkSavedPreparationBlocks = (savedPreparationTemplates) =>
  (savedPreparationTemplates || [])
    .map(normalizeSavedPreparationTemplate)
    .filter(Boolean)
    .sort((a, b) => a.preparationName.localeCompare(b.preparationName, undefined, { sensitivity: 'base' }));

// Bulk "Saved Preparations" section: EVERY preparation template in the DB with
// full batch quantities, rendered at the end of the book regardless of the
// selected category grouping. Populates outPageMap so the "(Saved Prep)"
// cross-reference annotations point at these pages.
const renderSavedPreparationsBulkSection = (pdf, savedPreparationTemplates, outPageMap = null) => {
  const preps = collectBulkSavedPreparationBlocks(savedPreparationTemplates);

  if (preps.length === 0) return;

  const pageHeight = pdf.internal.pageSize.getHeight();

  pdf.addPage();
  let yPos = 18;
  yPos = addSectionHeading(pdf, yPos, SAVED_PREPARATIONS_SECTION_HEADING);

  preps.forEach((prep, index) => {
    const estimatedHeight = estimateSavedPreparationBlockHeight(prep);
    if (yPos + estimatedHeight > pageHeight - 12) {
      pdf.addPage();
      yPos = 18;
      yPos = addSectionHeading(pdf, yPos, SAVED_PREPARATIONS_SECTION_HEADING);
    }

    if (outPageMap instanceof Map) {
      const key = normalizeKey(prep.preparationName);
      if (key && !outPageMap.has(key)) {
        outPageMap.set(key, pdf.internal.getCurrentPageInfo().pageNumber);
      }
    }

    yPos = renderSavedPrepTemplateBlock(pdf, yPos, prep, index + 1);
  });
};

// ── Saved Preparation Usage Summary ────────────────────────────────────────
// Inverts the recipe → preparation relationship: for every preparation template
// in the business, which menu items consume it and how much of it each takes.
// Preparations nothing maps to are still listed, so the section doubles as the
// full inventory of saved preparations.
const buildSavedPreparationUsageIndex = (entries, savedPreparationTemplates) => {
  const preps = (savedPreparationTemplates || [])
    .map(normalizeSavedPreparationTemplate)
    .filter(Boolean);

  if (preps.length === 0) return [];

  // preparation key -> (item + unit) -> usage row. Keying the inner map on the
  // unit too keeps a mixed-unit mapping honest instead of adding g to ml; the
  // usual case (one row per item) collapses to a single entry either way.
  const usageByPrep = new Map();

  (entries || []).forEach((entry) => {
    const itemName = toTitleCase(normalizeText(entry?.recipeName) || 'Recipe');
    const savedPreparations = Array.isArray(entry?.savedPreparations) ? entry.savedPreparations : [];

    savedPreparations.forEach((prep) => {
      const prepKey = normalizeKey(prep?.preparationName);
      if (!prepKey) return;

      const unit = normalizeText(prep?.usedUnit) || '-';
      const quantity = Number(prep?.usedQuantity);
      const rowKey = `${normalizeKey(itemName)}|${normalizeKey(unit)}`;

      if (!usageByPrep.has(prepKey)) usageByPrep.set(prepKey, new Map());
      const rows = usageByPrep.get(prepKey);

      const existing = rows.get(rowKey);
      if (existing) {
        // Same item listing the same preparation twice — report the total.
        existing.quantity += Number.isFinite(quantity) ? quantity : 0;
        return;
      }

      rows.set(rowKey, {
        itemName,
        quantity: Number.isFinite(quantity) ? quantity : 0,
        unit
      });
    });
  });

  return preps
    .map((prep) => {
      const rows = usageByPrep.get(normalizeKey(prep.preparationName));
      const usages = rows
        ? Array.from(rows.values()).sort((a, b) =>
            a.itemName.localeCompare(b.itemName, undefined, { sensitivity: 'base' })
          )
        : [];

      return {
        preparationName: prep.preparationName,
        totalQuantity: prep.totalQuantity,
        totalUnit: prep.totalUnit,
        usages
      };
    })
    .sort((a, b) => a.preparationName.localeCompare(b.preparationName, undefined, { sensitivity: 'base' }));
};

const formatBatchLabel = (totalQuantity, totalUnit) => (
  Number(totalQuantity) > 0
    ? `Batch - ${formatNumber(totalQuantity)} ${normalizeText(totalUnit)}`.trim()
    : 'Batch quantity not set'
);

const estimateSavedPreparationUsageBlockHeight = (prep) => {
  const usageRows = Math.max(1, Array.isArray(prep?.usages) ? prep.usages.length : 0);
  return 18 + 10 + 6 + (usageRows * 9) + 6;
};

// One block of the usage summary: preparation name + batch quantity, then the
// menu items consuming it with the quantity each takes.
const renderSavedPreparationUsageBlock = (pdf, startY, prep, itemIndex) => {
  let yPos = startY;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const leftX = 22;
  const rightX = pageWidth - 22;
  const colSerialX = leftX;
  const colNameX = leftX + 12;
  const colQtyX = rightX - 50;
  const colUnitX = rightX - 18;

  const usages = Array.isArray(prep?.usages) ? prep.usages : [];

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(0, 0, 0);
  pdf.text(`${itemIndex}. ${toTitleCase(prep?.preparationName || 'Saved Preparation')}`, 20, yPos + 4);

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);
  pdf.text(formatBatchLabel(prep?.totalQuantity, prep?.totalUnit), pageWidth - 20, yPos + 4, { align: 'right' });

  const usedByLabel = usages.length === 1 ? 'Used by 1 item' : `Used by ${usages.length} items`;
  pdf.setFont('helvetica', 'italic');
  pdf.setFontSize(8.5);
  pdf.setTextColor(100, 100, 100);
  pdf.text(usedByLabel, 20, yPos + 8);

  yPos += 14;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);
  pdf.text('S.No', colSerialX, yPos);
  pdf.text('ITEM NAME', colNameX, yPos);
  pdf.text('QTY USED', colQtyX, yPos, { align: 'right' });
  pdf.text('UNIT', colUnitX, yPos, { align: 'right' });

  yPos += 6;
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.4);
  pdf.line(leftX, yPos - 3, rightX, yPos - 3);

  if (usages.length === 0) {
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(10);
    pdf.setTextColor(120, 120, 120);
    pdf.text('Not used by any menu item', colNameX, yPos + 4);

    yPos += 9;
    pdf.setDrawColor(220, 220, 220);
    pdf.setLineWidth(0.2);
    pdf.line(leftX, yPos - 3, rightX, yPos - 3);

    return yPos + 4;
  }

  usages.forEach((usage, rowIndex) => {
    if (yPos > pageHeight - 28) {
      pdf.addPage();
      yPos = 18;
    }

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(80, 80, 80);
    pdf.text(String(rowIndex + 1), colSerialX, yPos + 4);

    pdf.setTextColor(0, 0, 0);
    pdf.text(String(usage?.itemName || ''), colNameX, yPos + 4);

    pdf.setFont('helvetica', 'bold');
    pdf.text(usage?.quantity > 0 ? formatNumber(usage.quantity) : '-', colQtyX, yPos + 4, { align: 'right' });

    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(String(usage?.unit || '-'), colUnitX, yPos + 4, { align: 'right' });

    yPos += 9;

    pdf.setDrawColor(220, 220, 220);
    pdf.setLineWidth(0.2);
    pdf.line(leftX, yPos - 3, rightX, yPos - 3);
  });

  return yPos + 4;
};

const SAVED_PREP_USAGE_HEADING = 'Saved Preparation Usage Summary';

const renderSavedPreparationUsageSection = (pdf, usageIndex) => {
  if (!Array.isArray(usageIndex) || usageIndex.length === 0) return;

  const pageHeight = pdf.internal.pageSize.getHeight();

  pdf.addPage();
  let yPos = 18;
  yPos = addSectionHeading(pdf, yPos, SAVED_PREP_USAGE_HEADING);

  usageIndex.forEach((prep, index) => {
    const estimatedHeight = estimateSavedPreparationUsageBlockHeight(prep);
    if (yPos + estimatedHeight > pageHeight - 12) {
      pdf.addPage();
      yPos = 18;
      yPos = addSectionHeading(pdf, yPos, SAVED_PREP_USAGE_HEADING);
    }

    yPos = renderSavedPreparationUsageBlock(pdf, yPos, prep, index + 1);
  });
};

// One complimentary item shaped into the block the detail renderers consume.
// Shared by the per-category sections and the pooled closing section so both
// present an item identically.
const toComplimentaryDetailBlock = (item) => {
  const ingredients = Array.isArray(item?.ingredients) ? item.ingredients : [];

  const normalizedIngredients = ingredients.length > 0
    ? ingredients.map((ingredient) => ({
        name: toTitleCase(ingredient?.name || 'Ingredient'),
        quantity: ingredient?.quantity,
        unit: normalizeText(ingredient?.unit) || '-'
      }))
    : [{ name: 'No ingredients mapped', quantity: '-', unit: '-' }];

  return {
    recipeName: toTitleCase(item?.name || 'Item'),
    ingredients: normalizedIngredients,
    yieldLabel: item?.yieldLabel || '1 Per Plate'
  };
};

// Every complimentary item linked anywhere in the book, de-duplicated by name
// and sorted — the source for the pooled "Complimentary Items" closing section
// and for its Table of Contents group, so the two always agree on order and
// numbering.
const collectPooledComplimentaryBlocks = (entries) => {
  const seenComplimentaries = new Set();
  const blocks = [];

  (entries || []).forEach((entry) => {
    const complimentaryItems = Array.isArray(entry?.complimentaryItems) ? entry.complimentaryItems : [];

    complimentaryItems.forEach((item) => {
      const lookupKey = normalizeKey(item?.name);
      if (!lookupKey || seenComplimentaries.has(lookupKey)) return;
      seenComplimentaries.add(lookupKey);

      blocks.push({ ...toComplimentaryDetailBlock(item), lookupKey });
    });
  });

  return blocks.sort((a, b) =>
    a.recipeName.localeCompare(b.recipeName, undefined, { sensitivity: 'base' })
  );
};

const COMPLIMENTARY_ITEMS_HEADING = 'Complimentary Items';

// Pooled "Complimentary Items" closing section: one section holding every
// complimentary item in the book, rendered the same way the per-category
// sections render a single item. Populates outPageMap with the page each item
// lands on so the Table of Contents can point at it.
const renderComplimentaryItemsBulkSection = (pdf, blocks, tableTheme, outPageMap = null) => {
  if (!Array.isArray(blocks) || blocks.length === 0) return;

  const pageHeight = pdf.internal.pageSize.getHeight();

  pdf.addPage();
  let yPos = 18;
  yPos = addSectionHeading(pdf, yPos, COMPLIMENTARY_ITEMS_HEADING);

  blocks.forEach((block, index) => {
    const syntheticEntry = {
      recipeName: block.recipeName,
      ingredients: block.ingredients,
      yieldLabel: block.yieldLabel,
      // Plain ingredient list — no raw/saved-prep split inside a detail block.
      sectioned: false
    };

    const estimatedHeight = estimateRecipeEntryHeight(syntheticEntry);
    if (yPos + estimatedHeight > pageHeight - 12) {
      pdf.addPage();
      yPos = 18;
      yPos = addSectionHeading(pdf, yPos, COMPLIMENTARY_ITEMS_HEADING);
    }

    if (outPageMap instanceof Map) {
      const key = block.lookupKey || normalizeKey(block.recipeName);
      if (key && !outPageMap.has(key)) {
        outPageMap.set(key, pdf.internal.getCurrentPageInfo().pageNumber);
      }
    }

    yPos = renderRecipeEntry(pdf, yPos, syntheticEntry, tableTheme, index + 1);
  });
};

// Consolidated view of every complimentary item in the book, the mirror of the
// saved-preparation usage summary: one block per item, never repeated, listing
// the menu items it accompanies and how much each one takes. The components a
// complimentary item is made of already have their own block in the pooled
// "Complimentary Items" section, so this section answers the other question —
// where the item is served.
const buildComplimentaryUsageIndex = (entries, groupMode = 'sub') => {
  const byComplimentary = new Map();

  (entries || []).forEach((entry) => {
    const itemName = toTitleCase(normalizeText(entry?.recipeName) || 'Recipe');
    const category = resolveEntryGroupName(entry, groupMode);
    const complimentaryItems = Array.isArray(entry?.complimentaryItems) ? entry.complimentaryItems : [];

    complimentaryItems.forEach((item) => {
      const key = normalizeKey(item?.name);
      if (!key) return;

      if (!byComplimentary.has(key)) {
        byComplimentary.set(key, {
          complimentaryName: toTitleCase(item?.name || 'Complimentary Item'),
          yieldLabel: item?.yieldLabel || '1 Per Plate',
          categories: new Map(),
          usages: new Map()
        });
      }

      const record = byComplimentary.get(key);
      const { quantity, unit } = summariseComplimentaryQuantity(item);

      // One row per menu item. A menu item that links the same complimentary
      // item twice reports the total rather than appearing twice.
      const rowKey = `${normalizeKey(category)}|${normalizeKey(itemName)}`;
      const existing = record.usages.get(rowKey);
      if (existing) {
        existing.quantity += Number.isFinite(quantity) ? quantity : 0;
        return;
      }

      record.usages.set(rowKey, {
        itemName,
        category,
        quantity: Number.isFinite(quantity) ? quantity : 0,
        unit
      });

      record.categories.set(category, (record.categories.get(category) || 0) + 1);
    });
  });

  return Array.from(byComplimentary.values())
    .map((record) => ({
      complimentaryName: record.complimentaryName,
      yieldLabel: record.yieldLabel,
      usages: Array.from(record.usages.values()).sort((a, b) =>
        a.itemName.localeCompare(b.itemName, undefined, { sensitivity: 'base' })
      ),
      categories: Array.from(record.categories.entries())
        .map(([category, count]) => ({ category, count }))
        .sort((a, b) => a.category.localeCompare(b.category, undefined, { sensitivity: 'base' }))
    }))
    .sort((a, b) =>
      a.complimentaryName.localeCompare(b.complimentaryName, undefined, { sensitivity: 'base' })
    );
};

// "Used in" reads as one wrapped paragraph, so its height depends on how many
// lines the category list needs once wrapped to the block width.
const complimentaryUsedInLines = (pdf, summary, maxWidth) => {
  const label = (summary?.categories || [])
    .map(({ category, count }) => `${category} (${count})`)
    .join(', ');

  if (!label) return [];
  return pdf.splitTextToSize(`Used in: ${label}`, maxWidth);
};

const estimateComplimentarySummaryBlockHeight = (summary) => {
  const usageRows = Math.max(1, Array.isArray(summary?.usages) ? summary.usages.length : 0);
  const categoryCount = Array.isArray(summary?.categories) ? summary.categories.length : 0;
  // Categories wrap at roughly three per line at this font size. The renderer
  // measures for real; this only has to be close enough to place the block.
  const usedInLines = categoryCount > 0 ? Math.ceil(categoryCount / 3) : 0;

  return 14 + 6 + (usageRows * 9) + (usedInLines * 5) + 8;
};

// One block of the complimentary summary: the item name and how many plates it
// accompanies, the menu items serving it with the quantity each takes, then the
// categories those items sit in.
const renderComplimentarySummaryBlock = (pdf, startY, summary, itemIndex) => {
  let yPos = startY;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const leftX = 22;
  const rightX = pageWidth - 22;
  const colSerialX = leftX;
  const colNameX = leftX + 12;
  const colQtyX = rightX - 50;
  const colUnitX = rightX - 18;

  const usages = Array.isArray(summary?.usages) ? summary.usages : [];
  const categoryCount = Array.isArray(summary?.categories) ? summary.categories.length : 0;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(0, 0, 0);
  pdf.text(`${itemIndex}. ${toTitleCase(summary?.complimentaryName || 'Complimentary Item')}`, 20, yPos + 4);

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.text(String(summary?.yieldLabel || '1 Per Plate'), pageWidth - 20, yPos + 4, { align: 'right' });

  const itemLabel = usages.length === 1 ? 'Used by 1 item' : `Used by ${usages.length} items`;
  const categoryLabel = categoryCount === 1 ? '1 category' : `${categoryCount} categories`;
  pdf.setFont('helvetica', 'italic');
  pdf.setFontSize(8.5);
  pdf.setTextColor(100, 100, 100);
  pdf.text(`${itemLabel} across ${categoryLabel}`, 20, yPos + 8);

  yPos += 14;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);
  pdf.text('S.No', colSerialX, yPos);
  pdf.text('ITEM NAME', colNameX, yPos);
  pdf.text('QTY USED', colQtyX, yPos, { align: 'right' });
  pdf.text('UNIT', colUnitX, yPos, { align: 'right' });

  yPos += 6;
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.4);
  pdf.line(leftX, yPos - 3, rightX, yPos - 3);

  if (usages.length === 0) {
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(10);
    pdf.setTextColor(120, 120, 120);
    pdf.text('Not served with any menu item', colNameX, yPos + 4);

    yPos += 9;
    pdf.setDrawColor(220, 220, 220);
    pdf.setLineWidth(0.2);
    pdf.line(leftX, yPos - 3, rightX, yPos - 3);

    pdf.setTextColor(0, 0, 0);
    return yPos + 4;
  }

  usages.forEach((usage, rowIndex) => {
    if (yPos > pageHeight - 28) {
      pdf.addPage();
      yPos = 18;
    }

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(80, 80, 80);
    pdf.text(String(rowIndex + 1), colSerialX, yPos + 4);

    pdf.setTextColor(0, 0, 0);
    pdf.text(String(usage?.itemName || ''), colNameX, yPos + 4);

    pdf.setFont('helvetica', 'bold');
    pdf.text(usage?.quantity > 0 ? formatNumber(usage.quantity) : '-', colQtyX, yPos + 4, { align: 'right' });

    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(String(usage?.unit || '-'), colUnitX, yPos + 4, { align: 'right' });

    yPos += 9;

    pdf.setDrawColor(220, 220, 220);
    pdf.setLineWidth(0.2);
    pdf.line(leftX, yPos - 3, rightX, yPos - 3);
  });

  pdf.setFont('helvetica', 'italic');
  pdf.setFontSize(8.5);
  pdf.setTextColor(90, 90, 90);

  complimentaryUsedInLines(pdf, summary, rightX - colNameX).forEach((line) => {
    if (yPos > pageHeight - 20) {
      pdf.addPage();
      yPos = 18;
    }

    pdf.text(line, colNameX, yPos + 3);
    yPos += 5;
  });

  pdf.setTextColor(0, 0, 0);

  return yPos + 4;
};

const COMPLIMENTARY_SUMMARY_HEADING = 'Complimentary Summary';

// Closing section listing every complimentary item exactly once, however many
// categories it is served with.
const renderComplimentarySummarySection = (pdf, summaryIndex) => {
  if (!Array.isArray(summaryIndex) || summaryIndex.length === 0) return;

  const pageHeight = pdf.internal.pageSize.getHeight();

  pdf.addPage();
  let yPos = 18;
  yPos = addSectionHeading(pdf, yPos, COMPLIMENTARY_SUMMARY_HEADING);

  summaryIndex.forEach((summary, index) => {
    const estimatedHeight = estimateComplimentarySummaryBlockHeight(summary);
    if (yPos + estimatedHeight > pageHeight - 12) {
      pdf.addPage();
      yPos = 18;
      yPos = addSectionHeading(pdf, yPos, COMPLIMENTARY_SUMMARY_HEADING);
    }

    yPos = renderComplimentarySummaryBlock(pdf, yPos, summary, index + 1);
  });
};

const CUSTOM_PLATES_HEADING = 'Custom Plates';

const scaleQuantity = (value, factor) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Number((numeric * factor).toFixed(4)) : value;
};

// Dishes saved in Item Mapping under "Custom plates" (e.g. 5 plates), re-shaped for
// the closing Custom Plates section. Every entry quantity is per plate, so each one is
// multiplied back up by the saved plate count — the numbers read exactly as they were
// entered. The dish's own section earlier in the book is untouched and stays per plate.
const buildCustomPlateEntries = (entries) =>
  (entries || [])
    .map((entry) => {
      const plates = Number(entry?.customPlateCount);
      if (!Number.isFinite(plates) || plates < 1) return null;

      return {
        ...entry,
        yieldLabel: `For ${formatNumber(plates)} ${plates === 1 ? 'Plate' : 'Plates'}`,
        ingredients: (entry.ingredients || []).map((ingredient) => ({
          ...ingredient,
          quantity: scaleQuantity(ingredient?.quantity, plates)
        })),
        savedPreparations: (entry.savedPreparations || []).map((prep) => ({
          ...prep,
          usedQuantity: scaleQuantity(prep?.usedQuantity, plates)
        })),
        complimentaryItems: (entry.complimentaryItems || []).map((item) => ({
          ...item,
          quantityMultiplier: scaleQuantity(item?.quantityMultiplier || 1, plates)
        }))
      };
    })
    .filter(Boolean)
    .sort((a, b) =>
      normalizeText(a?.recipeName).localeCompare(normalizeText(b?.recipeName), undefined, { sensitivity: 'base' })
    );

// The very last section of the Recipe Book: every Custom Plates dish with its
// quantities for the plate count it was saved under.
const renderCustomPlatesSection = (pdf, customPlateEntries, tableTheme) => {
  if (!Array.isArray(customPlateEntries) || customPlateEntries.length === 0) return;

  const pageHeight = pdf.internal.pageSize.getHeight();

  pdf.addPage();
  let yPos = 18;
  yPos = addSectionHeading(pdf, yPos, CUSTOM_PLATES_HEADING);

  customPlateEntries.forEach((entry, index) => {
    const estimatedHeight = estimateRecipeEntryHeight(entry);
    if (yPos + estimatedHeight > pageHeight - 12) {
      pdf.addPage();
      yPos = 18;
      yPos = addSectionHeading(pdf, yPos, CUSTOM_PLATES_HEADING);
    }

    yPos = renderRecipeEntry(pdf, yPos, entry, tableTheme, index + 1, null);
  });
};

const renderComplimentaryDetailSections = (pdf, entries, tableTheme, sectionPrefix, groupMode = 'sub') => {
  const groupedCategories = groupEntriesByCategory(entries, groupMode);
  const pageHeight = pdf.internal.pageSize.getHeight();

  groupedCategories.forEach((group) => {
    const blocks = [];
    const seenComplimentaries = new Set();

    group.items.forEach((entry) => {
      const complimentaryItems = Array.isArray(entry?.complimentaryItems) ? entry.complimentaryItems : [];

      complimentaryItems.forEach((item) => {
        const itemNameNormalized = normalizeKey(item.name);
        if (seenComplimentaries.has(itemNameNormalized)) return;
        seenComplimentaries.add(itemNameNormalized);

        blocks.push(toComplimentaryDetailBlock(item));
      });
    });

    if (blocks.length === 0) return;

    pdf.addPage();
    let yPos = 18;
    yPos = addSectionHeading(pdf, yPos, `Complimentary Details - ${group.category}`);

    blocks.forEach((block, index) => {
      const syntheticEntry = {
        recipeName: block.recipeName,
        ingredients: block.ingredients,
        yieldLabel: block.yieldLabel,
        // Plain ingredient list — no raw/saved-prep split inside a detail block.
        sectioned: false
      };

      const estimatedHeight = estimateRecipeEntryHeight(syntheticEntry);
      if (yPos + estimatedHeight > pageHeight - 12) {
        pdf.addPage();
        yPos = 18;
        yPos = addSectionHeading(pdf, yPos, `Complimentary Details - ${group.category}`);
      }

      yPos = renderRecipeEntry(pdf, yPos, syntheticEntry, tableTheme, index + 1);
    });
  });
};

const buildCustomizationDetailRows = (entries) => {
  const rows = [];

  (entries || []).forEach((entry) => {
    const category = toCategoryName(entry?.category);
    const recipeName = toTitleCase(entry?.recipeName || 'Recipe');
    const customizations = normalizeCustomizationNames(entry?.customizations);

    customizations.forEach((customization, index) => {
      rows.push([
        index === 0 ? category : '',
        index === 0 ? recipeName : '',
        toTitleCase(customization)
      ]);
    });
  });

  return rows;
};

const renderSavedPreparationDetailSections = (pdf, entries, tableTheme, sectionPrefix, groupMode = 'sub', outPageMap = null) => {
  const groupedCategories = groupEntriesByCategory(entries, groupMode);
  const pageHeight = pdf.internal.pageSize.getHeight();

  groupedCategories.forEach((group) => {
    const blocks = [];
    const seenPreps = new Set();

    group.items.forEach((entry) => {
      const savedPreparations = Array.isArray(entry?.savedPreparations) ? entry.savedPreparations : [];

      savedPreparations.forEach((preparation) => {
        const ingredients = Array.isArray(preparation?.ingredients) ? preparation.ingredients : [];
        if (ingredients.length === 0) return;

        const prepNameNormalized = normalizeKey(preparation.preparationName);
        if (seenPreps.has(prepNameNormalized)) return;
        seenPreps.add(prepNameNormalized);

        const yieldLabel = preparation.totalQuantity > 0
          ? `${formatNumber(preparation.totalQuantity)} ${preparation.totalUnit || ''}`.trim()
          : '';

        blocks.push({
          preparationName: preparation.preparationName,
          recipeName: toTitleCase(preparation.preparationName),
          ingredients,
          yieldLabel
        });
      });
    });

    if (blocks.length === 0) return;

    pdf.addPage();
    let yPos = 18;
    yPos = addSectionHeading(pdf, yPos, `Saved Preparation Details - ${group.category}`);

    blocks.forEach((block, index) => {
      const syntheticEntry = {
        recipeName: block.recipeName,
        ingredients: block.ingredients,
        yieldLabel: block.yieldLabel,
        isSavedPrepDetail: true,
        // Plain ingredient list — no raw/saved-prep split inside a detail block.
        sectioned: false
      };

      const estimatedHeight = estimateRecipeEntryHeight(syntheticEntry);
      if (yPos + estimatedHeight > pageHeight - 12) {
        pdf.addPage();
        yPos = 18;
        yPos = addSectionHeading(pdf, yPos, `Saved Preparation Details - ${group.category}`);
      }

      // Record the actual landing page for this saved preparation
      if (outPageMap instanceof Map) {
        const key = normalizeKey(block.preparationName);
        if (key && !outPageMap.has(key)) {
          outPageMap.set(key, pdf.internal.getCurrentPageInfo().pageNumber);
        }
      }

      yPos = renderRecipeEntry(pdf, yPos, syntheticEntry, tableTheme, index + 1);
    });
  });
};

const renderRecipeRelationDetailSections = (
  pdf,
  entries,
  tableTheme,
  sectionPrefix,
  groupMode = 'sub',
  savedPrepPageMap = null,
  { includeSavedPreparations = true, includeComplimentary = true } = {}
) => {
  // includeComplimentary is turned off when the book pools every complimentary
  // item into one closing section instead of one section per category.
  if (includeComplimentary) {
    renderComplimentaryDetailSections(pdf, entries, tableTheme, sectionPrefix, groupMode);
  }

  if (includeSavedPreparations) {
    renderSavedPreparationDetailSections(pdf, entries, tableTheme, sectionPrefix, groupMode, savedPrepPageMap);
  }

  const customizationRows = buildCustomizationDetailRows(entries);
  if (customizationRows.length > 0) {
    pdf.addPage();
    let yPos = 18;
    yPos = addSectionHeading(pdf, yPos, `${sectionPrefix} - Add-on Details`);

    autoTable(pdf, {
      ...tableTheme,
      startY: yPos,
      head: [['Category', 'Recipe', 'Add-on']],
      body: customizationRows,
      pageBreak: 'auto',
      rowPageBreak: 'auto'
    });
  }
};

const renderPreparationReviewSection = (pdf, preparationReviewDrafts, tableTheme, sectionLabel) => {
  if (!Array.isArray(preparationReviewDrafts) || preparationReviewDrafts.length === 0) return;

  pdf.addPage();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let yPos = 18;
  yPos = addSectionHeading(pdf, yPos, `${sectionLabel} - Saved Preparations`);

  preparationReviewDrafts.forEach((prep, index) => {
    const estimatedHeight = estimateSavedPreparationBlockHeight(prep);
    if (yPos + estimatedHeight > pageHeight - 12) {
      pdf.addPage();
      yPos = 18;
      yPos = addSectionHeading(pdf, yPos, `${sectionLabel} - Saved Preparations`);
    }

    yPos = renderSavedPreparationBlock(pdf, yPos, prep, index + 1);
  });
};

const enrichRecipeEntriesWithDetails = async (entries, resolvedBusinessId, savedPreparationLookup) => {
  return Promise.all(
    (entries || []).map(async (entry) => {
      const recipeId = normalizeText(entry?.recipeId);

      let ingredients = Array.isArray(entry?.ingredients) ? entry.ingredients : [];
      if (ingredients.length === 0 && recipeId) {
        try {
          const serverIngredients = await RecipesService.getRecipeIngredients(recipeId, resolvedBusinessId);
          ingredients = (serverIngredients || []).map(normalizeServerIngredient).filter(Boolean);
        } catch (error) {
          console.warn(`Failed to load ingredients for recipe ${recipeId}:`, error);
        }
      }

      const linkedComplimentaryItems = recipeId
        ? await loadLinkedComplimentaryItems(recipeId)
        : [];

      const selectedComplimentaryNames = Array.from(new Set([
        ...(Array.isArray(entry?.selectedComplimentaryNames) ? entry.selectedComplimentaryNames : []),
        ...(Array.isArray(entry?.recipeComplimentaryNames) ? entry.recipeComplimentaryNames : [])
      ]));

      return {
        ...entry,
        ingredients,
        complimentaryItems: buildComplimentarySummary(linkedComplimentaryItems, selectedComplimentaryNames),
        savedPreparations: buildSavedPreparationSummary(ingredients, savedPreparationLookup),
        customizations: normalizeCustomizationNames(entry?.customizations)
      };
    })
  );
};

const inferDraftSource = (draft) => {
  const source = normalizeKey(draft?.source || draft?.origin);

  if (source.includes('qr')) return 'qr-itemmap';
  if (source.includes('itemmap') || source.includes('item-map')) return 'itemmap';

  const hasCustomizations = Array.isArray(draft?.selectedCustomizations) && draft.selectedCustomizations.length > 0;
  return hasCustomizations ? 'qr-itemmap' : 'itemmap';
};

const buildReviewedRecipeEntries = async (
  resolvedBusinessId,
  recipes,
  recipeById,
  localRecipeDrafts,
  savedPreparationLookup
) => {
  const localRecipeEntries = (localRecipeDrafts || []).map((draft) => {
    const recipeIds = Array.isArray(draft?.recipeIds) ? draft.recipeIds : [];
    const resolvedRecipe = recipeIds.map((id) => recipeById.get(normalizeText(id))).find(Boolean);
    const resolvedRecipeId = resolvedRecipe ? resolveRecipeId(resolvedRecipe) : normalizeText(recipeIds[0]);

    const ingredients = Array.isArray(draft?.ingredientList)
      ? draft.ingredientList.map(normalizeDraftIngredient).filter(Boolean)
      : [];

    return {
      recipeId: resolvedRecipeId,
      recipeName: normalizeText(resolvedRecipe?.name || draft?.recipeName || (resolvedRecipeId ? `Recipe ${resolvedRecipeId}` : 'Unknown Recipe')),
      category: toCategoryName(resolvedRecipe?.category),
      masterCategory: normalizeText(resolvedRecipe?.master_category),
      menuPrice: Number(resolvedRecipe?.price),
      selectedComplimentaryNames: normalizeComplimentaryNames(draft?.selectedComplimentaryItems),
      recipeComplimentaryNames: normalizeComplimentaryNames(resolvedRecipe?.complimentary_items),
      customizations: normalizeCustomizationNames([
        ...(Array.isArray(draft?.selectedCustomizations) ? draft.selectedCustomizations : []),
        ...(Array.isArray(resolvedRecipe?.add_ons) ? resolvedRecipe.add_ons : [])
      ]),
      source: inferDraftSource(draft),
      ingredients
    };
  });

  const localRecipeIds = new Set(localRecipeEntries.map((entry) => normalizeText(entry.recipeId)).filter(Boolean));

  const dbDraftRecipeEntries = (recipes || [])
    .filter((recipe) => normalizeKey(recipe?.status) === 'draft')
    .map((recipe) => {
      const recipeId = resolveRecipeId(recipe);
      return {
        recipeId,
        recipeName: normalizeText(recipe?.name || (recipeId ? `Recipe ${recipeId}` : 'Unknown Recipe')),
        category: toCategoryName(recipe?.category),
        masterCategory: normalizeText(recipe?.master_category),
        selectedComplimentaryNames: [],
        recipeComplimentaryNames: normalizeComplimentaryNames(recipe?.complimentary_items),
        customizations: normalizeCustomizationNames(recipe?.add_ons),
        source: 'database',
        ingredients: (recipe.ingredients || []).map(normalizeServerIngredient).filter(Boolean)
      };
    })
    .filter((entry) => !localRecipeIds.has(normalizeText(entry.recipeId)));

  const reviewRecipeEntries = [...localRecipeEntries, ...dbDraftRecipeEntries];
  return enrichRecipeEntriesWithDetails(reviewRecipeEntries, resolvedBusinessId, savedPreparationLookup);
};

const buildAllRecipeEntries = async (resolvedBusinessId, recipes, localRecipeDrafts, savedPreparationLookup) => {
  // 1. Build a map of local drafts by recipeId
  const draftByRecipeId = new Map();
  (localRecipeDrafts || []).forEach(draft => {
    const rawIds = Array.isArray(draft.recipeIds) ? draft.recipeIds : (draft.recipeId ? [draft.recipeId] : []);
    rawIds.forEach(id => {
      if (id) draftByRecipeId.set(normalizeText(id), draft);
    });
  });

  const seenRecipeIds = new Set();

  // 2. Map database recipes, overriding with draft data if available
  const recipeEntries = (recipes || []).map((recipe) => {
    const recipeId = resolveRecipeId(recipe);
    const key = recipeId ? normalizeText(recipeId) : '';
    const draft = key ? draftByRecipeId.get(key) : null;
    
    if (key) seenRecipeIds.add(key);

    if (draft) {
      const draftIngs = Array.isArray(draft.ingredients)
        ? draft.ingredients.map(normalizeDraftIngredient).filter(Boolean)
        : [];

      return {
        recipeId,
        recipeName: normalizeText(draft.recipeName || recipe?.name || 'Unknown Recipe'),
        category: toCategoryName(draft.category || recipe?.category),
        masterCategory: normalizeText(recipe?.master_category),
        menuPrice: Number(recipe?.price),
        selectedComplimentaryNames: Array.isArray(draft.selectedComplimentaryItems) ? draft.selectedComplimentaryItems : [],
        recipeComplimentaryNames: normalizeComplimentaryNames(recipe?.complimentary_items),
        customizations: normalizeCustomizationNames(draft.selectedCustomizations),
        source: 'all-recipes',
        ingredients: draftIngs,
        prepTime: draft.prepTime !== undefined ? draft.prepTime : recipe?.prep_time_minutes,
        cookTime: draft.cookTime !== undefined ? draft.cookTime : recipe?.cook_time_minutes,
        servings: draft.servings !== undefined ? draft.servings : recipe?.servings_per_batch,
        customPlateCount: recipe?.custom_plate_count
      };
    }

    return {
      recipeId,
      recipeName: normalizeText(recipe?.name || 'Unknown Recipe'),
      category: toCategoryName(recipe?.category),
      masterCategory: normalizeText(recipe?.master_category),
      menuPrice: Number(recipe?.price),
      selectedComplimentaryNames: normalizeComplimentaryNames(recipe?.complimentary_items),
      recipeComplimentaryNames: normalizeComplimentaryNames(recipe?.complimentary_items),
      customizations: normalizeCustomizationNames(recipe?.add_ons),
      source: 'all-recipes',
      ingredients: (recipe.ingredients || []).map(normalizeServerIngredient).filter(Boolean),
      prepTime: recipe?.prep_time_minutes,
      cookTime: recipe?.cook_time_minutes,
      servings: recipe?.servings_per_batch,
      customPlateCount: recipe?.custom_plate_count
    };
  });

  // 3. Add any local drafts that are NOT in the database yet
  (localRecipeDrafts || []).forEach(draft => {
    const recipeId = Array.isArray(draft.recipeIds) ? draft.recipeIds[0] : (draft.recipeId || null);
    const key = recipeId ? normalizeText(recipeId) : '';
    if (key && !seenRecipeIds.has(key)) {
      seenRecipeIds.add(key);
      const draftIngs = Array.isArray(draft.ingredients)
        ? draft.ingredients.map(normalizeDraftIngredient).filter(Boolean)
        : [];

      recipeEntries.push({
        recipeId,
        recipeName: normalizeText(draft.recipeName || 'Unknown Recipe'),
        category: toCategoryName(draft.category),
        masterCategory: normalizeText(draft.masterCategory),
        menuPrice: 0,
        selectedComplimentaryNames: Array.isArray(draft.selectedComplimentaryItems) ? draft.selectedComplimentaryItems : [],
        recipeComplimentaryNames: [],
        customizations: normalizeCustomizationNames(draft.selectedCustomizations),
        source: 'all-recipes',
        ingredients: draftIngs,
        prepTime: draft.prepTime,
        cookTime: draft.cookTime,
        servings: draft.servings
      });
    }
  });

  return enrichRecipeEntriesWithDetails(recipeEntries, resolvedBusinessId, savedPreparationLookup);
};

const partitionReviewedEntriesBySource = (reviewedRecipeEntries) => {
  const itemMapEntries = [];
  const qrItemMapEntries = [];
  const otherEntries = [];

  (reviewedRecipeEntries || []).forEach((entry) => {
    const source = normalizeKey(entry?.source);

    if (source === 'qr-itemmap') {
      qrItemMapEntries.push(entry);
      return;
    }

    if (source === 'itemmap') {
      itemMapEntries.push(entry);
      return;
    }

    otherEntries.push(entry);
  });

  return { itemMapEntries, qrItemMapEntries, otherEntries };
};

const getDateStamp = () => new Date().toISOString().slice(0, 10);

// All saved preparation templates from the DB, normalized to
// {preparationName, totalQuantity, totalUnit, ingredients} with full batch
// quantities and sorted by name. Shared by the JSON backup so it stays in
// sync with the PDF/Excel saved-preparation sections.
export const fetchSavedPreparationsForExport = async (businessId) => {
  const resolvedBusinessId = getResolvedBusinessId(businessId);
  if (!resolvedBusinessId) return [];

  const templates = await loadSavedPreparationTemplates(resolvedBusinessId);
  return (templates || [])
    .map(normalizeSavedPreparationTemplate)
    .filter(Boolean)
    .sort((a, b) => a.preparationName.localeCompare(b.preparationName, undefined, { sensitivity: 'base' }));
};

export const exportMarkedReviewPdf = async ({ businessId } = {}) => {
  const resolvedBusinessId = getResolvedBusinessId(businessId);
  if (!resolvedBusinessId) {
    throw new Error('Business ID not found. Please login again.');
  }

  const [recipes, localRecipeDrafts, preparationReviewDrafts, savedPreparationTemplates] = await Promise.all([
    RecipesService.getExportData(resolvedBusinessId),
    Promise.resolve(listRecipeReviewDrafts(resolvedBusinessId)),
    Promise.resolve(readPreparationReviewDrafts(resolvedBusinessId)),
    loadSavedPreparationTemplates(resolvedBusinessId)
  ]);

  const recipeById = buildRecipeLookup(recipes || []);
  const savedPreparationLookup = buildSavedPreparationLookup(savedPreparationTemplates);

  const reviewedRecipeEntries = await buildReviewedRecipeEntries(
    resolvedBusinessId,
    recipes,
    recipeById,
    localRecipeDrafts,
    savedPreparationLookup
  );

  const hasRecipeReviews = reviewedRecipeEntries.length > 0;
  const hasPreparationReviews = preparationReviewDrafts.length > 0;

  if (!hasRecipeReviews && !hasPreparationReviews) {
    throw new Error('No items marked as review found in local or database.');
  }

  const reviewedCategories = groupEntriesByCategory(reviewedRecipeEntries).length;
  const reviewedIngredientRows = reviewedRecipeEntries.reduce(
    (sum, item) => sum + (Array.isArray(item.ingredients) ? item.ingredients.length : 0),
    0
  );

  const reviewedSavedPreparationRows = reviewedRecipeEntries.reduce(
    (sum, item) => sum + getSavedPreparationRowCount(item),
    0
  );

  const preparationIngredientRows = preparationReviewDrafts.reduce(
    (sum, prep) => sum + (Array.isArray(prep.ingredients) ? prep.ingredients.length : 0),
    0
  );

  const pdf = new jsPDF('p', 'mm', 'a4');
  const tableTheme = createTableTheme();

  const generatedLabel = new Date().toLocaleString();
  const fileTimestamp = getDateStamp();

  const summaryStartY = drawPdfHeader(
    pdf,
    'Mark as Review Report',
    `Business ID: ${resolvedBusinessId} | Generated: ${generatedLabel}`
  );

  autoTable(pdf, {
    ...tableTheme,
    startY: summaryStartY,
    head: [['Metric', 'Count']],
    body: [
      ['Reviewed Categories', reviewedCategories],
      ['Reviewed Recipes', reviewedRecipeEntries.length],
      ['Reviewed Saved Preparations', preparationReviewDrafts.length],
      ['Ingredient Rows Included', reviewedIngredientRows + reviewedSavedPreparationRows + preparationIngredientRows]
    ],
    pageBreak: 'avoid',
    rowPageBreak: 'avoid'
  });

  if (reviewedRecipeEntries.length > 0) {
    const { itemMapEntries, qrItemMapEntries, otherEntries } = partitionReviewedEntriesBySource(reviewedRecipeEntries);

    if (itemMapEntries.length > 0) {
      renderRecipesByCategory(pdf, itemMapEntries, 'Mark as Review - Item Map', tableTheme);
    }

    if (qrItemMapEntries.length > 0) {
      renderRecipesByCategory(pdf, qrItemMapEntries, 'Mark as Review - QR Item Map', tableTheme);
    }

    if (otherEntries.length > 0) {
      renderRecipesByCategory(pdf, otherEntries, 'Mark as Review - Other Drafts', tableTheme);
    }
  }

  if (preparationReviewDrafts.length > 0) {
    renderPreparationReviewSection(pdf, preparationReviewDrafts, tableTheme, 'Mark as Review');
  }

  renderRecipeRelationDetailSections(pdf, reviewedRecipeEntries, tableTheme, 'Mark as Review');

  addPdfFooters(pdf);
  pdf.save(`Mark_As_Review_${fileTimestamp}.pdf`);
};

/**
 * Build the Recipe Book PDF.
 *
 * @param {'save'|'blob'} [output='save']  'save' downloads it (the original behaviour,
 *   which every existing caller relies on). 'blob' returns the document instead so it
 *   can be previewed in-app — see components/RecipeBook/RecipePdfViewer.js.
 * @returns {Promise<{blob: Blob|null, fileName: string, pageCount: number}>}
 */
export const exportAllRecipesPdf = async ({ businessId, startDate, endDate, groupMode = 'sub', output = 'save' } = {}) => {
  const resolvedBusinessId = getResolvedBusinessId(businessId);
  if (!resolvedBusinessId) {
    throw new Error('Business ID not found. Please login again.');
  }

  const [recipes, localRecipeDrafts, preparationReviewDrafts, savedPreparationTemplates] = await Promise.all([
    RecipesService.getExportData(resolvedBusinessId, startDate, endDate),
    Promise.resolve(listRecipeReviewDrafts(resolvedBusinessId)),
    Promise.resolve(readPreparationReviewDrafts(resolvedBusinessId)),
    loadSavedPreparationTemplates(resolvedBusinessId)
  ]);

  const recipeById = buildRecipeLookup(recipes || []);
  const savedPreparationLookup = buildSavedPreparationLookup(savedPreparationTemplates);

  const [allRecipeEntries, reviewedRecipeEntries] = await Promise.all([
    buildAllRecipeEntries(resolvedBusinessId, recipes, localRecipeDrafts, savedPreparationLookup),
    buildReviewedRecipeEntries(resolvedBusinessId, recipes, recipeById, localRecipeDrafts, savedPreparationLookup)
  ]);

  if (allRecipeEntries.length === 0) {
    throw new Error('No recipes found to export.');
  }

  const businessDisplayName = await resolveBusinessDisplayName(recipes, resolvedBusinessId);
  const groupedAllCategories = groupEntriesByCategory(allRecipeEntries, groupMode);

  const pdf = new jsPDF('p', 'mm', 'a4');
  const tableTheme = createTableTheme();

  // Business 173 pools every complimentary item into one closing section and
  // lists both closing sections in the Table of Contents. Everywhere else the
  // complimentary items stay split per category and the contents page lists
  // menu items only.
  const pooledClosingSections = usesPooledClosingSections(resolvedBusinessId);

  const pooledComplimentaryBlocks = pooledClosingSections
    ? collectPooledComplimentaryBlocks(allRecipeEntries)
    : [];

  // Contents rows for the closing sections, plus the slots their page numbers
  // are written into once those sections have been rendered.
  const closingSectionTocGroups = pooledClosingSections
    ? buildClosingSectionTocGroups(
        pooledComplimentaryBlocks,
        collectBulkSavedPreparationBlocks(savedPreparationTemplates)
      )
    : [];
  const deferredTocPageRefs = [];

  // 1. Calculate how many pages the Table of Contents itself needs
  const tocPages = simulateTableOfContentsPages([...groupedAllCategories, ...closingSectionTocGroups]);

  // 2. Simulate the layout starting immediately after the Table of Contents pages
  // Page 1 = Cover, Pages 2 to 1 + tocPages = TOC, so Recipes start at 2 + tocPages
  const { recipePageRanges } = simulateLayout(groupedAllCategories, 1 + tocPages);

  // Page 1: Draw the cover page
  drawCoverPage(pdf, businessDisplayName, allRecipeEntries.length);

  // Pre-allocate the Table of Contents pages
  for (let p = 1; p <= tocPages; p++) {
    pdf.addPage();
  }

  // Draw Table of Contents on the pre-allocated pages (starting at Page 2)
  pdf.setPage(2);
  drawTableOfContents(pdf, groupedAllCategories, recipePageRanges, tableTheme, {
    extraGroups: closingSectionTocGroups,
    deferredPageRefs: deferredTocPageRefs
  });

  // Return active page to the end of the Table of Contents before rendering recipes
  pdf.setPage(1 + tocPages);

  // 3. savedPrepPageMap: populated with ACTUAL page numbers as the bulk
  //    "Saved Preparations" section renders (renderSavedPreparationsBulkSection).
  //    pendingAnnotations: collects the exact PDF coordinates of each "(Saved Prep)"
  //    ingredient row so we can go back and add the correct page number AFTER rendering.
  const savedPrepPageMap = new Map();
  const complimentaryPageMap = new Map();
  const pendingAnnotations = [];

  renderRecipesByCategory(
    pdf,
    allRecipeEntries,
    'All Recipes',
    tableTheme,
    { showSectionPrefix: false, groupMode },
    pendingAnnotations
  );

  if (reviewedRecipeEntries.length > 0) {
    const { itemMapEntries, qrItemMapEntries, otherEntries } = partitionReviewedEntriesBySource(reviewedRecipeEntries);

    if (itemMapEntries.length > 0) {
      renderRecipesByCategory(pdf, itemMapEntries, 'Mark as Review - Item Map', tableTheme, { groupMode });
    }

    if (qrItemMapEntries.length > 0) {
      renderRecipesByCategory(pdf, qrItemMapEntries, 'Mark as Review - QR Item Map', tableTheme, { groupMode });
    }

    if (otherEntries.length > 0) {
      renderRecipesByCategory(pdf, otherEntries, 'Mark as Review - Other Drafts', tableTheme, { groupMode });
    }
  }

  if (preparationReviewDrafts.length > 0) {
    renderPreparationReviewSection(pdf, preparationReviewDrafts, tableTheme, 'Mark as Review');
  }

  // Complimentary + add-on detail sections. Saved preps are NOT rendered per
  // category here — they get one bulk section at the very end instead, and for
  // pooled books the complimentary items get one too.
  renderRecipeRelationDetailSections(
    pdf,
    allRecipeEntries,
    tableTheme,
    'All Recipes',
    groupMode,
    null,
    {
      includeSavedPreparations: false,
      includeComplimentary: !pooledClosingSections
    }
  );

  // Closing section: every complimentary item in the book in one list, with the
  // page each lands on recorded for the Table of Contents.
  if (pooledClosingSections) {
    renderComplimentaryItemsBulkSection(
      pdf,
      pooledComplimentaryBlocks,
      tableTheme,
      complimentaryPageMap
    );
  }

  // Closing section: ALL saved preparation templates from the DB with their full
  // batch quantities, independent of the master/sub grouping choice.
  // Populates savedPrepPageMap with the actual page numbers for annotations.
  renderSavedPreparationsBulkSection(pdf, savedPreparationTemplates, savedPrepPageMap);

  // The same preparations inverted — batch quantity plus every menu item that
  // consumes them and how much each one takes.
  renderSavedPreparationUsageSection(
    pdf,
    buildSavedPreparationUsageIndex(allRecipeEntries, savedPreparationTemplates)
  );

  // Very last section, for books that pool their complimentary items: each
  // complimentary item once, with its quantities and the categories it serves.
  if (pooledClosingSections) {
    renderComplimentarySummarySection(
      pdf,
      buildComplimentaryUsageIndex(allRecipeEntries, groupMode)
    );
  }

  // Last page(s): dishes saved under Custom Plates in Item Mapping, at the plate count
  // they were saved with. Rendered after every other section so no earlier page moves.
  renderCustomPlatesSection(pdf, buildCustomPlateEntries(allRecipeEntries), tableTheme);

  // Now savedPrepPageMap has the correct actual page numbers.
  // Go back to each recorded Saved Prep ingredient row and append the page reference.
  if (pendingAnnotations.length > 0) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pendingAnnotations.forEach(({ page, x, y, baseName }) => {
      const actualPage = savedPrepPageMap.get(baseName);
      if (actualPage !== undefined) {
        pdf.setPage(page);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        pdf.text(` - pg no ${actualPage}`, x, y);
      }
    });
  }

  // The closing sections have rendered, so their real page numbers are known —
  // fill in the contents rows that were held back for them.
  drawDeferredTocRows(pdf, deferredTocPageRefs, {
    complimentary: complimentaryPageMap,
    savedPreparations: savedPrepPageMap
  });

  // Skip page number footers on the cover page and all Table of Contents pages
  const skipPages = new Set([1]);
  for (let p = 2; p <= 1 + tocPages; p++) {
    skipPages.add(p);
  }
  addPdfFooters(pdf, { skipPages });

  const filePrefix = toFileSafeName(`${businessDisplayName} Recipe Book`);
  const fileName = `${filePrefix}.pdf`;

  // THE DOWNLOAD LIVES HERE, and nowhere else.
  //
  // The recipe book is generated entirely in the browser — there is no backend PDF
  // endpoint — so `pdf.save()` IS the download: jsPDF builds a blob and clicks a
  // hidden anchor at it. That is why "view the book" used to be impossible: by the
  // time this function returned, the file was already in the Downloads folder.
  //
  // `output` splits the two intentions apart:
  //   'save' (default) — unchanged behaviour, byte-identical file, every existing
  //                      caller keeps working without being touched
  //   'blob'           — hand the document back so it can be shown in a viewer, and
  //                      let the VIEWER decide if and when to save it
  if (output === 'blob') {
    return { blob: pdf.output('blob'), fileName, pageCount: pdf.getNumberOfPages() };
  }

  pdf.save(fileName);
  return { blob: null, fileName, pageCount: pdf.getNumberOfPages() };
};

// ── Excel design tokens — International Klein Blue "recipe book" theme (#002FA7 accent) ──
const XLS_COLOR = {
  band: '002FA7',      // recipe / section band (International Klein Blue)
  header: 'D6DEF6',    // column-header row (light blue tint)
  total: 'E7EBFA',     // pale blue totals
  bandInk: 'FFFFFF',   // white text on the deep blue bands
  headerInk: '002FA7', // deep blue text on the light header rows
  ink: '2A2A2A',       // body text
  muted: '6B6B6B',     // subtle text
  border: 'BFBFBF',    // thin grey gridline
  title: 'FFFFFF'      // title text on the blue title band
};

const PRICE_FMT = '"₹"#,##0.00';
const QTY_FMT = '#,##0.###';
const INT_FMT = '#,##0';

// Excel always renders the decimal separator of '#,##0.###', so whole numbers
// would display with a trailing dot ("50."). Pick the integer format for them.
const qtyFmtFor = (value) => (Number.isInteger(value) ? INT_FMT : QTY_FMT);

const xlsThin = { style: 'thin', color: { rgb: XLS_COLOR.border } };
const xlsAllBorders = { top: xlsThin, bottom: xlsThin, left: xlsThin, right: xlsThin };

// Ultramarine band used for sheet titles and per-recipe headers.
const xlsBandStyle = (align = 'center', numFmt = null) => {
  const style = {
    font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: XLS_COLOR.bandInk } },
    fill: { patternType: 'solid', fgColor: { rgb: XLS_COLOR.band } },
    alignment: { horizontal: align, vertical: 'center' },
    border: xlsAllBorders
  };
  if (numFmt) style.numFmt = numFmt;
  return style;
};

// Larger ultramarine band for the sheet-level title.
const xlsSheetTitleStyle = () => ({
  font: { name: 'Calibri', sz: 14, bold: true, color: { rgb: XLS_COLOR.bandInk } },
  fill: { patternType: 'solid', fgColor: { rgb: XLS_COLOR.band } },
  alignment: { horizontal: 'center', vertical: 'center' },
  border: xlsAllBorders
});

// Light-blue column header (Ingredient / Quantity / Unit).
const xlsColHeaderStyle = (align = 'left') => ({
  font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: XLS_COLOR.headerInk } },
  fill: { patternType: 'solid', fgColor: { rgb: XLS_COLOR.header } },
  alignment: { horizontal: align, vertical: 'center' },
  border: xlsAllBorders
});

// White body cell.
const xlsBodyStyle = ({ align = 'left', numFmt = null, muted = false, italic = false } = {}) => {
  const style = {
    font: { name: 'Calibri', sz: 10, italic, color: { rgb: muted ? XLS_COLOR.muted : XLS_COLOR.ink } },
    alignment: { horizontal: align, vertical: 'center' },
    border: xlsAllBorders
  };
  if (numFmt) style.numFmt = numFmt;
  return style;
};

const xlsTitleStyle = () => ({
  font: { name: 'Calibri', sz: 16, bold: true, color: { rgb: XLS_COLOR.title } },
  alignment: { horizontal: 'center', vertical: 'center' },
  fill: { patternType: 'solid', fgColor: { rgb: XLS_COLOR.band } },
  border: xlsAllBorders
});

const xlsSubtitleStyle = () => ({
  font: { name: 'Calibri', sz: 10, italic: true, color: { rgb: XLS_COLOR.muted } },
  alignment: { horizontal: 'center', vertical: 'center' }
});

const xlsTotalStyle = (align = 'left', numFmt = null) => {
  const style = {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: XLS_COLOR.ink } },
    fill: { patternType: 'solid', fgColor: { rgb: XLS_COLOR.total } },
    alignment: { horizontal: align, vertical: 'center' },
    border: xlsAllBorders
  };
  if (numFmt) style.numFmt = numFmt;
  return style;
};

const setXlsCell = (ws, row, col, value, style, numFmt) => {
  const addr = XLSXStyle.utils.encode_cell({ r: row, c: col });
  const isNumber = typeof value === 'number' && Number.isFinite(value);
  const cell = {
    t: isNumber ? 'n' : 's',
    v: value === null || value === undefined ? '' : value
  };
  if (style) cell.s = style;
  if (numFmt) cell.z = numFmt;
  ws[addr] = cell;
  return addr;
};

// Apply a style to every cell of a horizontal span so merged bands render
// their fill + border across the full width (covered cells need styling too).
const styleXlsSpan = (ws, row, fromCol, toCol, style) => {
  for (let c = fromCol; c <= toCol; c += 1) {
    const addr = XLSXStyle.utils.encode_cell({ r: row, c });
    if (!ws[addr]) ws[addr] = { t: 's', v: '' };
    ws[addr].s = style;
  }
};

// Excel sheet names: max 31 chars, no : \ / ? * [ ], must be unique.
const sanitizeSheetName = (name, usedLower) => {
  const base = (normalizeText(name).replace(/[\\/?*[\]:]/g, ' ').replace(/\s+/g, ' ').trim() || 'Sheet').slice(0, 31);
  let candidate = base;
  let i = 2;
  while (usedLower.has(candidate.toLowerCase())) {
    const suffix = ` (${i})`;
    candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    i += 1;
  }
  usedLower.add(candidate.toLowerCase());
  return candidate;
};

// Pale-blue sub-band separating "Raw Ingredients" from "Saved Preparations"
// inside a recipe block — mirrors the PDF's section dividers.
const xlsSectionStyle = () => ({
  font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: XLS_COLOR.headerInk } },
  fill: { patternType: 'solid', fgColor: { rgb: XLS_COLOR.total } },
  alignment: { horizontal: 'left', vertical: 'center' },
  border: xlsAllBorders
});

// One "<label> / Quantity / Unit" block: sub-band, column headers, then rows.
// Returns the next free row index.
const writeXlsComponentSection = (ws, merges, startRow, label, nameHeader, lines, emptyText) => {
  let row = startRow;

  setXlsCell(ws, row, 0, label, xlsSectionStyle());
  styleXlsSpan(ws, row, 0, 2, xlsSectionStyle());
  merges.push({ s: { r: row, c: 0 }, e: { r: row, c: 2 } });
  row += 1;

  setXlsCell(ws, row, 0, nameHeader, xlsColHeaderStyle('left'));
  setXlsCell(ws, row, 1, 'Quantity', xlsColHeaderStyle('right'));
  setXlsCell(ws, row, 2, 'Unit', xlsColHeaderStyle('center'));
  row += 1;

  if (!lines || lines.length === 0) {
    setXlsCell(ws, row, 0, emptyText, xlsBodyStyle({ align: 'center', muted: true, italic: true }));
    styleXlsSpan(ws, row, 0, 2, xlsBodyStyle({ align: 'center', muted: true, italic: true }));
    merges.push({ s: { r: row, c: 0 }, e: { r: row, c: 2 } });
    return row + 1;
  }

  lines.forEach((line) => {
    const qty = typeof line.quantity === 'number' && Number.isFinite(line.quantity) ? line.quantity : '';
    setXlsCell(ws, row, 0, line.displayName || line.name, xlsBodyStyle({ align: 'left' }));
    setXlsCell(ws, row, 1, qty, xlsBodyStyle({ align: 'right', numFmt: qtyFmtFor(qty) }), qty === '' ? undefined : qtyFmtFor(qty));
    setXlsCell(ws, row, 2, line.unit || '-', xlsBodyStyle({ align: 'center' }));
    row += 1;
  });

  return row;
};

export const exportAllRecipesXlsx = async ({ businessId, startDate, endDate, groupMode = 'sub' } = {}) => {
  const resolvedBusinessId = getResolvedBusinessId(businessId);
  if (!resolvedBusinessId) {
    throw new Error('Business ID not found. Please login again.');
  }

  // Uses the exact same recipe data pipeline as exportAllRecipesPdf (Recipe Download)
  // so the Excel output stays in sync with the PDF export.
  const [recipes, localRecipeDrafts, savedPreparationTemplates] = await Promise.all([
    RecipesService.getExportData(resolvedBusinessId, startDate, endDate),
    Promise.resolve(listRecipeReviewDrafts(resolvedBusinessId)),
    loadSavedPreparationTemplates(resolvedBusinessId)
  ]);

  const savedPreparationLookup = buildSavedPreparationLookup(savedPreparationTemplates);

  const allRecipeEntries = await buildAllRecipeEntries(
    resolvedBusinessId,
    recipes,
    localRecipeDrafts,
    savedPreparationLookup
  );

  if (allRecipeEntries.length === 0) {
    throw new Error('No recipes found to export.');
  }

  const businessDisplayName = await resolveBusinessDisplayName(recipes, resolvedBusinessId);
  const groupedAllCategories = groupEntriesByCategory(allRecipeEntries, groupMode);

  // Pre-compute the raw-ingredient / saved-preparation split per entry (used by
  // the per-category sheets) and the per-category recipe counts shown in the
  // summary breakdown.
  const sectionsByEntry = new Map();
  let totalSavedPreparationUsages = 0;
  const categoryStats = groupedAllCategories.map((group) => {
    group.items.forEach((entry) => {
      const sections = buildRecipeExportSections(entry);
      sectionsByEntry.set(entry, sections);
      totalSavedPreparationUsages += sections.savedPreparations.length;
    });
    return { category: group.category, recipes: group.items.length };
  });

  const workbook = XLSXStyle.utils.book_new();
  const usedSheetNames = new Set();

  // ═══════════════════════════════════════════════════════════════════
  // SHEET 1 — SUMMARY (cover: title + category breakdown)
  // ═══════════════════════════════════════════════════════════════════
  const summary = {};
  const summaryMerges = [];
  let r = 0;

  setXlsCell(summary, r, 0, `${businessDisplayName} — Recipe Book`, xlsTitleStyle());
  styleXlsSpan(summary, r, 0, 1, xlsTitleStyle());
  summaryMerges.push({ s: { r, c: 0 }, e: { r, c: 1 } });
  r += 1;
  setXlsCell(summary, r, 0, 'Recipe ingredients & details', xlsSubtitleStyle());
  styleXlsSpan(summary, r, 0, 1, xlsSubtitleStyle());
  summaryMerges.push({ s: { r, c: 0 }, e: { r, c: 1 } });
  r += 2;

  // Category breakdown section
  setXlsCell(summary, r, 0, 'CATEGORY BREAKDOWN', xlsBandStyle('left'));
  styleXlsSpan(summary, r, 0, 1, xlsBandStyle('left'));
  summaryMerges.push({ s: { r, c: 0 }, e: { r, c: 1 } });
  r += 1;
  setXlsCell(summary, r, 0, 'Category', xlsColHeaderStyle('left'));
  setXlsCell(summary, r, 1, 'Recipes', xlsColHeaderStyle('right'));
  r += 1;
  categoryStats.forEach((stat) => {
    setXlsCell(summary, r, 0, stat.category, xlsBodyStyle());
    setXlsCell(summary, r, 1, stat.recipes, xlsBodyStyle({ align: 'right', numFmt: INT_FMT }), INT_FMT);
    r += 1;
  });
  setXlsCell(summary, r, 0, 'Total', xlsTotalStyle('left'));
  setXlsCell(summary, r, 1, allRecipeEntries.length, xlsTotalStyle('right', INT_FMT), INT_FMT);
  r += 1;
  setXlsCell(summary, r, 0, 'Saved preparations used by items', xlsBodyStyle());
  setXlsCell(summary, r, 1, totalSavedPreparationUsages, xlsBodyStyle({ align: 'right', numFmt: INT_FMT }), INT_FMT);

  summary['!ref'] = XLSXStyle.utils.encode_range({ s: { r: 0, c: 0 }, e: { r, c: 1 } });
  summary['!cols'] = [{ wch: 32 }, { wch: 16 }];
  summary['!merges'] = summaryMerges;
  summary['!rows'] = [{ hpt: 26 }, { hpt: 16 }];
  XLSXStyle.utils.book_append_sheet(workbook, summary, sanitizeSheetName('Summary', usedSheetNames));

  // ═══════════════════════════════════════════════════════════════════
  // ONE SHEET PER CATEGORY — green recipe bands + ingredient rows
  // (Ingredient | Quantity | Unit), mirroring the barista-stock layout.
  // ═══════════════════════════════════════════════════════════════════
  groupedAllCategories.forEach((group) => {
    const ws = {};
    const merges = [];
    let row = 0;

    // Sheet title band (category name)
    setXlsCell(ws, row, 0, String(group.category).toUpperCase(), xlsSheetTitleStyle());
    styleXlsSpan(ws, row, 0, 2, xlsSheetTitleStyle());
    merges.push({ s: { r: row, c: 0 }, e: { r: row, c: 2 } });
    row += 1;

    const recipeWord = group.items.length === 1 ? 'recipe' : 'recipes';
    setXlsCell(ws, row, 0, `${group.items.length} ${recipeWord}`, xlsSubtitleStyle());
    styleXlsSpan(ws, row, 0, 2, xlsSubtitleStyle());
    merges.push({ s: { r: row, c: 0 }, e: { r: row, c: 2 } });
    row += 2; // blank spacer

    group.items.forEach((entry) => {
      const recipeName = toTitleCase(entry?.recipeName || 'Recipe');
      const price = Number(entry?.menuPrice ?? entry?.price);
      const hasPrice = Number.isFinite(price);

      // Recipe band: name (spans A:B, centered) + price (C, right)
      setXlsCell(ws, row, 0, recipeName, xlsBandStyle('center'));
      styleXlsSpan(ws, row, 0, 1, xlsBandStyle('center'));
      merges.push({ s: { r: row, c: 0 }, e: { r: row, c: 1 } });
      setXlsCell(ws, row, 2, hasPrice ? price : '', xlsBandStyle('right', hasPrice ? PRICE_FMT : null), hasPrice ? PRICE_FMT : undefined);
      row += 1;

      const sections = sectionsByEntry.get(entry) || { rawIngredients: [], savedPreparations: [] };

      row = writeXlsComponentSection(
        ws,
        merges,
        row,
        'Raw Ingredients',
        'Ingredient',
        sections.rawIngredients,
        'No ingredients mapped'
      );

      // Saved preparations used by this item, with the quantity + unit consumed.
      // Their own component breakdown lives on the "Saved Preparations" sheet.
      if (sections.savedPreparations.length > 0) {
        row = writeXlsComponentSection(
          ws,
          merges,
          row,
          'Saved Preparations',
          'Saved Preparation',
          sections.savedPreparations,
          'No saved preparations used'
        );
      }

      row += 1; // blank spacer between recipes
    });

    const lastRow = Math.max(row - 1, 0);
    ws['!ref'] = XLSXStyle.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: lastRow, c: 2 } });
    ws['!cols'] = [{ wch: 42 }, { wch: 14 }, { wch: 12 }];
    ws['!rows'] = [{ hpt: 24 }];
    ws['!merges'] = merges;
    XLSXStyle.utils.book_append_sheet(workbook, ws, sanitizeSheetName(group.category, usedSheetNames));
  });

  // ═══════════════════════════════════════════════════════════════════
  // FINAL SHEET — SAVED PREPARATIONS: every template in the DB with its
  // full batch quantities, independent of the category grouping (mirrors
  // the PDF's closing "Saved Preparations" section).
  // ═══════════════════════════════════════════════════════════════════
  const savedPreps = (savedPreparationTemplates || [])
    .map(normalizeSavedPreparationTemplate)
    .filter(Boolean)
    .sort((a, b) => a.preparationName.localeCompare(b.preparationName, undefined, { sensitivity: 'base' }));

  if (savedPreps.length > 0) {
    const ws = {};
    const merges = [];
    let row = 0;

    setXlsCell(ws, row, 0, 'SAVED PREPARATIONS', xlsSheetTitleStyle());
    styleXlsSpan(ws, row, 0, 2, xlsSheetTitleStyle());
    merges.push({ s: { r: row, c: 0 }, e: { r: row, c: 2 } });
    row += 1;

    const prepWord = savedPreps.length === 1 ? 'preparation' : 'preparations';
    setXlsCell(ws, row, 0, `${savedPreps.length} ${prepWord} · full batch quantities`, xlsSubtitleStyle());
    styleXlsSpan(ws, row, 0, 2, xlsSubtitleStyle());
    merges.push({ s: { r: row, c: 0 }, e: { r: row, c: 2 } });
    row += 2; // blank spacer

    savedPreps.forEach((prep) => {
      const batchLabel = prep.totalQuantity > 0
        ? `Batch - ${formatNumber(prep.totalQuantity)} ${prep.totalUnit || ''}`.trim()
        : '';

      // Preparation band: name (spans A:B, centered) + batch yield (C, right)
      setXlsCell(ws, row, 0, toTitleCase(prep.preparationName), xlsBandStyle('center'));
      styleXlsSpan(ws, row, 0, 1, xlsBandStyle('center'));
      merges.push({ s: { r: row, c: 0 }, e: { r: row, c: 1 } });
      setXlsCell(ws, row, 2, batchLabel, xlsBandStyle('right'));
      row += 1;

      // Column headers
      setXlsCell(ws, row, 0, 'Ingredient', xlsColHeaderStyle('left'));
      setXlsCell(ws, row, 1, 'Quantity', xlsColHeaderStyle('right'));
      setXlsCell(ws, row, 2, 'Unit', xlsColHeaderStyle('center'));
      row += 1;

      if (prep.ingredients.length === 0) {
        setXlsCell(ws, row, 0, 'No ingredients saved', xlsBodyStyle({ align: 'center', muted: true, italic: true }));
        styleXlsSpan(ws, row, 0, 2, xlsBodyStyle({ align: 'center', muted: true, italic: true }));
        merges.push({ s: { r: row, c: 0 }, e: { r: row, c: 2 } });
        row += 1;
      } else {
        prep.ingredients.forEach((ingredient) => {
          setXlsCell(ws, row, 0, toTitleCase(ingredient.name), xlsBodyStyle({ align: 'left' }));
          setXlsCell(ws, row, 1, ingredient.quantity, xlsBodyStyle({ align: 'right', numFmt: qtyFmtFor(ingredient.quantity) }), qtyFmtFor(ingredient.quantity));
          setXlsCell(ws, row, 2, ingredient.unit || '-', xlsBodyStyle({ align: 'center' }));
          row += 1;
        });
      }

      row += 1; // blank spacer between preparations
    });

    const lastPrepRow = Math.max(row - 1, 0);
    ws['!ref'] = XLSXStyle.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: lastPrepRow, c: 2 } });
    ws['!cols'] = [{ wch: 42 }, { wch: 14 }, { wch: 12 }];
    ws['!rows'] = [{ hpt: 24 }];
    ws['!merges'] = merges;
    XLSXStyle.utils.book_append_sheet(workbook, ws, sanitizeSheetName('Saved Preparations', usedSheetNames));
  }

  // ═══════════════════════════════════════════════════════════════════
  // LAST SHEET — PREP USAGE SUMMARY: each preparation with its batch
  // quantity and every menu item consuming it (mirrors the PDF's closing
  // "Saved Preparation Usage Summary" section).
  // ═══════════════════════════════════════════════════════════════════
  const usageIndex = buildSavedPreparationUsageIndex(allRecipeEntries, savedPreparationTemplates);

  if (usageIndex.length > 0) {
    const ws = {};
    const merges = [];
    let row = 0;

    setXlsCell(ws, row, 0, 'SAVED PREPARATION USAGE SUMMARY', xlsSheetTitleStyle());
    styleXlsSpan(ws, row, 0, 2, xlsSheetTitleStyle());
    merges.push({ s: { r: row, c: 0 }, e: { r: row, c: 2 } });
    row += 1;

    const usedCount = usageIndex.filter((prep) => prep.usages.length > 0).length;
    setXlsCell(
      ws,
      row,
      0,
      `${usageIndex.length} preparations · ${usedCount} used by menu items · ${totalSavedPreparationUsages} mappings`,
      xlsSubtitleStyle()
    );
    styleXlsSpan(ws, row, 0, 2, xlsSubtitleStyle());
    merges.push({ s: { r: row, c: 0 }, e: { r: row, c: 2 } });
    row += 2; // blank spacer

    usageIndex.forEach((prep) => {
      // Preparation band: name (spans A:B, centered) + batch quantity (C, right)
      setXlsCell(ws, row, 0, toTitleCase(prep.preparationName), xlsBandStyle('center'));
      styleXlsSpan(ws, row, 0, 1, xlsBandStyle('center'));
      merges.push({ s: { r: row, c: 0 }, e: { r: row, c: 1 } });
      setXlsCell(ws, row, 2, formatBatchLabel(prep.totalQuantity, prep.totalUnit), xlsBandStyle('right'));
      row += 1;

      setXlsCell(ws, row, 0, 'Item Name', xlsColHeaderStyle('left'));
      setXlsCell(ws, row, 1, 'Quantity Used', xlsColHeaderStyle('right'));
      setXlsCell(ws, row, 2, 'Unit', xlsColHeaderStyle('center'));
      row += 1;

      if (prep.usages.length === 0) {
        setXlsCell(ws, row, 0, 'Not used by any menu item', xlsBodyStyle({ align: 'center', muted: true, italic: true }));
        styleXlsSpan(ws, row, 0, 2, xlsBodyStyle({ align: 'center', muted: true, italic: true }));
        merges.push({ s: { r: row, c: 0 }, e: { r: row, c: 2 } });
        row += 1;
      } else {
        prep.usages.forEach((usage) => {
          const qty = Number.isFinite(usage.quantity) && usage.quantity > 0 ? usage.quantity : '';
          setXlsCell(ws, row, 0, usage.itemName, xlsBodyStyle({ align: 'left' }));
          setXlsCell(ws, row, 1, qty, xlsBodyStyle({ align: 'right', numFmt: qtyFmtFor(qty) }), qty === '' ? undefined : qtyFmtFor(qty));
          setXlsCell(ws, row, 2, usage.unit || '-', xlsBodyStyle({ align: 'center' }));
          row += 1;
        });

        setXlsCell(ws, row, 0, `Total - ${prep.usages.length} ${prep.usages.length === 1 ? 'item' : 'items'}`, xlsTotalStyle('left'));
        setXlsCell(ws, row, 1, '', xlsTotalStyle('right'));
        setXlsCell(ws, row, 2, '', xlsTotalStyle('center'));
        row += 1;
      }

      row += 1; // blank spacer between preparations
    });

    const lastUsageRow = Math.max(row - 1, 0);
    ws['!ref'] = XLSXStyle.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: lastUsageRow, c: 2 } });
    ws['!cols'] = [{ wch: 42 }, { wch: 16 }, { wch: 12 }];
    ws['!rows'] = [{ hpt: 24 }];
    ws['!merges'] = merges;
    XLSXStyle.utils.book_append_sheet(workbook, ws, sanitizeSheetName('Prep Usage Summary', usedSheetNames));
  }

  const filePrefix = toFileSafeName(`${businessDisplayName} Recipe Book`);
  XLSXStyle.writeFile(workbook, `${filePrefix}.xlsx`);
};

export const exportIngredientsTemplateXlsx = async ({ businessId } = {}) => {
  const resolvedBusinessId = getResolvedBusinessId(businessId);
  if (!resolvedBusinessId) {
    throw new Error('Business ID not found. Please login again.');
  }

  const [response, overviewResponse] = await Promise.all([
    InventoryService.getInventoryItems(resolvedBusinessId, 5000),
    InventoryService
      .getInventoryOverview(resolvedBusinessId, { includeInactive: true, includeComplimentary: true })
      .catch(() => null)
  ]);

  const inventoryItems = Array.isArray(response?.data)
    ? response.data
    : (Array.isArray(response) ? response : []);

  const overviewItems = Array.isArray(overviewResponse?.data)
    ? overviewResponse.data
    : (Array.isArray(overviewResponse?.items)
      ? overviewResponse.items
      : (Array.isArray(overviewResponse) ? overviewResponse : []));

  const unitByItemId = new Map();
  const unitByName = new Map();

  (overviewItems || []).forEach((item) => {
    const unit = normalizeText(
      item?.unit_symbol
      || item?.unitSymbol
      || item?.standardUnit
      || item?.standard_unit_symbol
      || item?.unit
    );
    if (!unit) return;

    const idKey = normalizeText(item?.item_id || item?.id);
    const nameKey = normalizeKey(item?.item_name || item?.name);

    if (idKey && !unitByItemId.has(idKey)) {
      unitByItemId.set(idKey, unit);
    }

    if (nameKey && !unitByName.has(nameKey)) {
      unitByName.set(nameKey, unit);
    }
  });

  if (inventoryItems.length === 0) {
    throw new Error('No ingredients found for this business.');
  }

  const rows = inventoryItems
    .map((item) => {
      const ingredientName = normalizeText(item?.name);
      const itemId = normalizeText(item?.id || item?.item_id);
      const ingredientNameKey = normalizeKey(ingredientName || item?.item_name);
      const dbUnit = normalizeText(
        item?.standardUnit
        || item?.unit_symbol
        || item?.unitSymbol
        || item?.standard_unit_symbol
        || item?.unit
        || unitByItemId.get(itemId)
        || unitByName.get(ingredientNameKey)
      );

      return {
        ingredientName,
        qty: '',
        unit: dbUnit
      };
    })
    .filter((row) => row.ingredientName)
    .sort((a, b) => a.ingredientName.localeCompare(b.ingredientName, undefined, { sensitivity: 'base' }));

  if (rows.length === 0) {
    throw new Error('No ingredients found for this business.');
  }

  const worksheetData = [
    ['Ingredient Name', 'Qty', 'Unit'],
    ...rows.map((row) => [row.ingredientName, row.qty, row.unit])
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  worksheet['!cols'] = [
    { wch: 42 },
    { wch: 12 },
    { wch: 12 }
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Ingredients');

  const fileTimestamp = getDateStamp();
  XLSX.writeFile(workbook, `Ingredients_Template_${resolvedBusinessId}_${fileTimestamp}.xlsx`);
};
